import React, { useEffect, useState, Suspense, useRef } from 'react';
import safeToast from './utils/safeToast';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Shield, Eye, User as UserIcon, Crown, Settings, ArrowLeft, X, Briefcase, Bell, ChevronRight, Check, Trash2 } from 'lucide-react';
// For dynamic templates in left-hand preview
import { PreviewCard as MetaPreviewCard } from './components/templates/MetaPreviewCard';
// Classic fallback preview + variant resolver
import { PreviewCard, resolveVariant } from './components/templates/PreviewCard';
import { DEFAULT_COVER } from './constants/media';
import { getAdminSiteSettings, updateAdminSiteSettings, type SubscriptionRules as AdminSubscriptionRules } from './api/siteSettings';
// Payment settings API (hydrate gateways)
import { getPaymentSettings, updatePaymentSettings, paymentSettingsToGateways } from './api/paymentSettings';
import { dispatchOwnerTokenEvent, OWNER_TOKEN_EVENT } from './utils/ownerTokenEvents';
import { mergeAuthenticationSettings } from './utils/authenticationSettings';
import { clearNonAdminSessionTokens } from './utils/pickAuthToken';
import {
  clearSignupSourceFlags,
  getAuthBackHomeUrl,
  isMiniwebsiteSignupComplete,
  markDigitalCardSignupSource,
} from './utils/signupRedirect';
import { syncWebAppManifest } from './utils/syncWebAppManifest';
// default homepage/auth settings and mock fallbacks
import {
  defaultHomepageSettings,
  defaultAuthenticationSettings,
  mockSystemStats,
  mockSubscriptionPlans,
  mockUsers,
  mockAds,
  mockLeads,
  mockPaymentGateways,
  mockEmailGateways,
  mockSMSGateways,
  mockCaptchaSettings as defaultCaptchaSettings,
  mockEmailOTPSettings as defaultEmailOTPSettings,
} from './data/mockData';
// Route-level chunks (lazy): keeps heavy routes off the initial bundle; MiniWebsite landing is eager for fast marketing URL.
// Missing admin sub-panels direct imports (managers/requests) used in inline routing branches
import SystemAdminManagersPanel from './components/admin/SystemAdminManagersPanel';
import SystemAdminRequestsPanel from './components/admin/SystemAdminRequestsPanel';
// Service layer imports (migrated fetch calls)
import { adminLogin, userLogin, requestPasswordReset, adminSession as serviceAdminSession, abandonIncomplete, deleteAdminSession } from './services/auth';
import { getPaymentConfig } from './services/payments';
import { incrementMetric as serviceIncrementMetric, getSystemStats as serviceGetSystemStats, getGlobalSummary as serviceGetGlobalSummary, subscribeEvents } from './services/analytics';
import { getSiteSettings, saveSiteSettings, getTemplatesMeta, getTemplateMetaById } from './services/templates';
import { listSubscriptionPlans, createSubscriptionPlan, updateSubscriptionPlan } from './services/subscriptions';
// Other admin components referenced in inline branches
import BasicAdminPanelV2 from './components/admin/BasicAdminPanelV2';
import AdminLogin from './components/admin/AdminLogin';
// E-card templates used directly in renderTemplate fallback
import { ECardTemplate1 } from './components/ECardTemplate1';
import { ECardTemplate2 } from './components/ECardTemplate2';
import { ECardTemplate3 } from './components/ECardTemplate3';
import { ECardTemplate4 } from './components/ECardTemplate4';
import { ECardTemplate5 } from './components/ECardTemplate5';

import {
  User,
  AdConfig,
  PaymentGateway,
  Lead,
  SubscriptionPlan,
  HomepageSettings,
  CaptchaSettings,
  EmailOTPSettings
} from './types';
import { getUser, updateUserByIdOrUsername, updateUser } from './api/users';
import { getAdminSessionUser } from './api/auth';
import { getCrmAccessStatus } from './api/crm';
import { deleteUserByAdmin } from './api/adminUsers';
// replaced local analytics increment with serviceIncrementMetric
// Removed legacy HomepageCustomization import to avoid stale codepaths
import ErrorBoundary from './components/ErrorBoundary';
import type { PaymentPlan } from './components/PaymentPanel';
/** Eager: avoids second load state on /miniwebsite (HTML splash + lazy chunk). */
import MiniWebsiteLanding from './components/miniwebsite/MiniWebsiteLanding';
/** Eager: Digital Card product landing + template gallery */
import DigitalCardLanding from './components/digitalcard/DigitalCardLanding';
import TemplatesGalleryPage from './components/digitalcard/TemplatesGalleryPage';
import DigitalCardContactPage from './components/digitalcard/DigitalCardContactPage';
import {
  captureTemplateFromUrl,
  clearPreSignupTemplate,
  loadPreSignupTemplate,
  preSignupToSelectPayload,
} from './utils/templateOnboarding';
/** Eager: same for /crm-login — only the HTML glowing-T splash should show while booting. */
import CrmLoginPage from './components/crm/CrmLoginPage';

/** Logo-less fallback: HTML boot splash already owns the T; a second logo causes a visible flash. */
const RouteChunkFallback: React.FC = () => (
  <div className="min-h-screen bg-[#0b0b22]" aria-busy="true" />
);

const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const DownloadPage = React.lazy(() => import('./pages/DownloadPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const DeleteAccountPage = React.lazy(() => import('./pages/DeleteAccountPage'));
const RefundPolicyPage = React.lazy(() => import('./pages/RefundPolicyPage'));
const HomePage = React.lazy(() => import('./components/HomePage').then((m) => ({ default: m.HomePage })));
const MaintenancePage = React.lazy(() => import('./components/MaintenancePage'));
const AuthPage = React.lazy(() => import('./components/AuthPage').then((m) => ({ default: m.AuthPage })));
const TemplateSelection = React.lazy(() => import('./components/TemplateSelection').then((m) => ({ default: m.TemplateSelection })));
const SetPassword = React.lazy(() => import('./components/SetPassword'));
const CardPreview = React.lazy(() => import('./components/CardPreview').then((m) => ({ default: m.CardPreview })));
const PublicCardViewer = React.lazy(() => import('./components/PublicCardViewer').then((m) => ({ default: m.PublicCardViewer })));
const ContactExchange = React.lazy(() => import('./components/crm/ContactExchange'));
const CardOwnerPanel = React.lazy(() => import('./components/CardOwnerPanel').then((m) => ({ default: m.CardOwnerPanel })));
const SystemAdminPanel = React.lazy(() => import('./components/SystemAdminPanel').then((m) => ({ default: m.SystemAdminPanel })));
const PaymentPanel = React.lazy(() => import('./components/PaymentPanel'));
import { PlanSelectionStep } from './components/PlanSelectionStep';
import OwnerMiniWebsiteButton from './components/OwnerMiniWebsiteButton';
import { LanguageSwitcher } from './components/LanguageSwitcher';

// Mobile Sales App (lazy-loaded)
const MobileApp = React.lazy(() => import('./mobile/MobileApp'));
// CRM panel (lazy); login is eager — see import above
const CrmPanelPage = React.lazy(() => import('./components/crm/CrmPanelPage'));
// Mini Website pages (lazy-loaded)
const PublicMiniWebsite = React.lazy(() => import('./components/miniwebsite/PublicMiniWebsite'));
const MiniWebsiteDashboard = React.lazy(() => import('./components/miniwebsite/MiniWebsiteDashboard'));
const WebsiteOwnerLogin = React.lazy(() => import('./components/miniwebsite/WebsiteOwnerLogin'));
import { persistManualLanguage, normalizeUiLang } from './utils/localePreference';
import { SiteBrandingProvider, useSiteBranding } from './context/SiteBrandingContext';
import { mergeHomepageSettings } from './utils/siteBranding';
import i18n from './i18n';
import { useTranslation } from 'react-i18next';
import { createLead as apiCreateLead, listLeadsByOwner, listAllLeads, markLeadAsRead as apiMarkLeadAsRead, deleteLead as apiDeleteLead } from './api/leads';

/*
  Summary:
  - This file is a cleaned / refactored version of the original App.tsx you provided.
  - Key fixes:
    - Removed duplicate imports.
    - Removed/merged duplicated states and duplicated function declarations.
    - Fixed stale closure bugs when updating subscriptionPlans by using functional updates.
    - Added safety/guard checks for possibly undefined users (displayUser/currentUser).
    - Removed stray/invalid tokens (there was a stray `customSettings={homepageSettings}` in a non-JSX location).
    - Provided onLogout callback so the dashboard's logout doesn't forcibly reload the page.
    - Removed the direct /dashboard route that attempted to render AdminDashboard without required props.
    - Consolidated localStorage read/write and guarded JSON parsing.
    - Made update/add/delete subscription plan handlers update homepage preview immediately and persist consistently.
    - Simplified renderTemplate switch and provided a safe fallback if template id is missing.
*/

/* ---------- Auth Flow Component ---------- */
const AuthFlow: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<'home' | 'auth' | 'template' | 'plan' | 'payment' | 'dashboard'>('home');
  // Persist selected plan across steps
  const [selectedPlan, setSelectedPlan] = useState<PaymentPlan | null>(() => {
    try { const raw = localStorage.getItem('selectedPlan'); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  useEffect(() => { try { if (selectedPlan) localStorage.setItem('selectedPlan', JSON.stringify(selectedPlan)); else localStorage.removeItem('selectedPlan'); } catch {} }, [selectedPlan]);
  const [authType, setAuthType] = useState<'admin' | 'user'>('user');
  const [authInitialView, setAuthInitialView] = useState<'login' | 'signup'>('login');
  const [userData, setUserData] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState<User | null>(null);
  const [isAdminCreated, setIsAdminCreated] = useState(false);
  const [homepageSettings, setHomepageSettings] = useState(defaultHomepageSettings);
  const [authenticationSettings, setAuthenticationSettings] = useState(defaultAuthenticationSettings);
  const [_editingSubscriptionPlans, _setEditingSubscriptionPlans] = useState(false);
  const [_adminActiveTab, _setAdminActiveTab] = useState('overview');
  // Pause global refreshes while user is entering sensitive checkout info
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const checkoutBusyRef = useRef(false);
  useEffect(() => { checkoutBusyRef.current = checkoutBusy; }, [checkoutBusy]);

  // Deep-link support: allow resuming signup via query params
  useEffect(() => {
    try {
      const usp = new URLSearchParams(window.location.search);

      // PesaPal TCard subscription return: show a success toast and clean up the URL param
      if (usp.get('payment_success') === '1') {
        const provider = usp.get('provider') || 'payment';
        safeToast('success', `Payment confirmed via ${provider}. Your subscription is now active!`);
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('payment_success');
        cleanUrl.searchParams.delete('provider');
        window.history.replaceState({}, '', cleanUrl.toString());
      }
      if (usp.get('payment_pending') === '1') {
        safeToast('info', 'Payment is being processed. Your subscription will activate shortly.');
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('payment_pending');
        cleanUrl.searchParams.delete('txId');
        window.history.replaceState({}, '', cleanUrl.toString());
      }

      // Direct panel login deep-links: ?panel=owner|admin|basic-admin|system-admin & login=1
      const panelParam = (usp.get('panel') || usp.get('target') || usp.get('role') || '').toLowerCase();
      const wantsLogin = ['1','true','yes','signin','login'].includes(String(usp.get('login') || usp.get('signin') || '').toLowerCase());
      const wantsSignup = ['1','true','yes'].includes(String(usp.get('signup') || usp.get('register') || '').toLowerCase());
      const wantsAdmin = ['1','true','yes'].includes(String(usp.get('admin') || '').toLowerCase());
      const wantsDashboard = ['1','true','yes'].includes(String(usp.get('dashboard') || '').toLowerCase());
      const ownerPanel = panelParam === 'owner' || panelParam === 'user' || panelParam === 'card-owner';
      // Card-owner panel deep-link: ?panel=owner&dashboard=1 (Digital Card Panel button, not mini-website signup)
      if (wantsDashboard && ownerPanel) {
        try { localStorage.setItem('dev_admin_initial_mode', 'card_owner'); } catch {}
      } else if (wantsAdmin) {
        setAuthType('admin');
        setCurrentStep('auth');
      } else if (panelParam) {
        if (ownerPanel) {
          setAuthType('user');
          setCurrentStep('auth');
          if (wantsSignup) {
            clearSignupSourceFlags();
            markDigitalCardSignupSource();
            setAuthInitialView('signup');
          }
        } else if (panelParam === 'admin' || panelParam === 'system-admin' || panelParam === 'basic-admin') {
          setAuthType('admin');
          setCurrentStep('auth');
          try { localStorage.setItem('admin_login_intent', panelParam); } catch {}
        }
      } else if (wantsSignup) {
        clearSignupSourceFlags();
        markDigitalCardSignupSource();
        setAuthType('user');
        setCurrentStep('auth');
        setAuthInitialView('signup');
      } else if (wantsLogin) {
        // Default to user login when only login=1 provided
        setAuthType('user');
        setCurrentStep('auth');
      }
      const templateParam = usp.get('template');
      if (templateParam) {
        captureTemplateFromUrl();
        markDigitalCardSignupSource();
      }
      const resume = usp.get('complete-signup') || usp.get('resume');
      const email = usp.get('email');
      if ((resume === '1' || resume === 'signup') && email) {
        // Prefill signup progress with email and switch to auth/signup
        const KEY = 'signup_progress_v1';
        let payload: any = {};
        try { payload = JSON.parse(localStorage.getItem(KEY) || '{}'); } catch {}
        payload.userForm = { ...(payload.userForm || {}), email };
        localStorage.setItem(KEY, JSON.stringify(payload));
        setAuthType('user');
        setCurrentStep('auth');
      }
    } catch {}
  }, []);

  // load homepage settings (prefer server public endpoint; no mock/default plan injection)
  useEffect(() => {
    (async () => {
      // Pricing fallback centralization:
      // BasicAdminPanelV2 consumes pricing via homepageSettings from this component.
      // We intentionally keep fallback logic here (and cache in localStorage as server_plans_cache)
      // so individual admin panels do not each implement their own plan recovery paths.
      // BasicAdminPanelClean implements a lightweight mirror but defers to this cache when empty.
      const getFallbackPlans = (): any[] => {
        try {
          const raw = localStorage.getItem('server_plans_cache');
          if (raw) {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr) && arr.length > 0) return arr;
          }
        } catch {}
        return Array.isArray(defaultHomepageSettings.pricing?.plans) ? defaultHomepageSettings.pricing.plans : [];
      };

      // 1) Try public server settings (+ subscription plans in parallel to cut latency)
      try {
        const plansPromise = fetch('/api/public/subscription-plans').catch(() => null as Response | null);
        const [data, resp] = await Promise.all([getSiteSettings(), plansPromise]);
        let settings = mergeHomepageSettings(data?.homepageSettings || {});
        
        // Load emailOTP settings from server if available and persist to localStorage
        if (settings.emailOTP) {
          try { localStorage.setItem('emailOTPSettings', JSON.stringify(settings.emailOTP)); } catch {}
        }
        
        // Merge authoritative active subscription plans from public endpoint (same response handling as before)
        try {
          if (!resp) {
            console.warn('[App] Error fetching public subscription plans');
          } else if (resp.ok) {
            const plans = await resp.json();
            if (Array.isArray(plans)) {
              if (plans.length > 0) {
                settings = { ...settings, pricing: { ...(settings.pricing || {}), plans } };
                try { localStorage.setItem('server_plans_cache', JSON.stringify(plans)); } catch {}
              } else {
                console.warn('[App] No subscription plans returned by server (active + fallback empty). Using defaults.');
              }
            } else {
              console.warn('[App] Unexpected subscription plans payload; expected array.');
            }
          } else {
            console.warn('[App] Failed to fetch public subscription plans endpoint', resp.status);
          }
        } catch (e) {
          console.warn('[App] Error fetching public subscription plans', e);
        }
        // Persist full homepage settings snapshot (including pricing) for offline usage
        try {
          localStorage.setItem('homepageSettings', JSON.stringify(settings));
        } catch {}
        setHomepageSettings(settings);
        return;
      } catch {}

      // 2) Fallback to localStorage snapshot
      try {
        const raw = localStorage.getItem('homepageSettings');
        if (raw) {
          const parsed = JSON.parse(raw);
          setHomepageSettings(mergeHomepageSettings(parsed));
          return;
        }
      } catch (err) {
        console.error('Error loading homepage settings from localStorage', err);
      }

      // 3) Default (enhanced): if we have cached server plans, prefer them even with no prior homepageSettings snapshot
      try {
        const rawPlans = localStorage.getItem('server_plans_cache');
        if (rawPlans) {
          let arr: any = null;
          try { arr = JSON.parse(rawPlans); } catch {}
          if (Array.isArray(arr) && arr.length > 0) {
            const merged = {
              ...defaultHomepageSettings,
              pricing: {
                ...(defaultHomepageSettings.pricing || {}),
                plans: arr
              }
            };
            console.info('[App] Offline fallback: using cached server subscription plans (server unreachable).');
            setHomepageSettings(merged);
            return;
          }
        }
      } catch {}
      setHomepageSettings(defaultHomepageSettings);
    })();
  }, []);

  // Listen for local homepageSettings updates (especially maintenance toggle)
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === 'homepageSettings' || e.key === 'homepageSettings_update_ts') {
        try {
          const raw = localStorage.getItem('homepageSettings');
          const localHp = raw ? JSON.parse(raw) : null;
          if (localHp) {
            // Preserve pricing.plans using precedence: local -> prev -> cached server -> defaults
            setHomepageSettings((prev: any) => ({
              ...(prev || {}),
              ...localHp,
              maintenance: { ...((prev || {}).maintenance || {}), ...(localHp.maintenance || {}) },
              pricing: (() => {
                const parsedPricing = (localHp as any)?.pricing || {};
                const parsedPlans = Array.isArray((parsedPricing as any)?.plans) && (parsedPricing as any).plans.length > 0 ? (parsedPricing as any).plans : null;
                if (parsedPlans) return { ...parsedPricing, plans: parsedPlans };
                const prevPlans = (prev as any)?.pricing?.plans;
                if (Array.isArray(prevPlans) && prevPlans.length > 0) return { ...parsedPricing, plans: prevPlans };
                try {
                  const rawCache = localStorage.getItem('server_plans_cache');
                  if (rawCache) {
                    const arr = JSON.parse(rawCache);
                    if (Array.isArray(arr) && arr.length > 0) return { ...parsedPricing, plans: arr };
                  }
                } catch {}
                return { ...parsedPricing, plans: Array.isArray(defaultHomepageSettings.pricing?.plans) ? defaultHomepageSettings.pricing.plans : [] };
              })()
            }));
          }
        } catch {}
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Public/live page sync: listen for homepageSettings changes from other tabs and refresh view (no default plan injection)
  // Strict admin route guard: block admin UI if no valid session
  useEffect(() => {
    const path = window.location.pathname || '';
    const isAdminRoute = /^\/admin(\/|$)/.test(path) || ['overview','basic','managers','requests','homepage','home-customization','subscriptions','membership-plans'].some(t => path === `/admin/${t}`);
    if (!isAdminRoute) return;
    (async () => {
      try {
        const token = localStorage.getItem('dev_admin_token');
        if (!token) {
          // no token: redirect to home
          try {
            localStorage.removeItem('dev_admin_role');
            localStorage.removeItem('dev_admin_initial_tab');
            localStorage.removeItem('dev_admin_initial_mode');
          } catch {}
          window.location.replace('/');
          return;
        }
  const sess = await serviceAdminSession(token);
  if (!sess.ok) {
          try {
            localStorage.removeItem('dev_admin_token');
            localStorage.removeItem('dev_admin_role');
            localStorage.removeItem('dev_admin_initial_tab');
            localStorage.removeItem('dev_admin_initial_mode');
            dispatchOwnerTokenEvent();
          } catch {}
          window.location.replace('/');
          return;
        }
  const s = (sess.data || ({})) as any;
        const role = s?.role;
        if (role !== 'superadmin' && role !== 'basicadmin') {
          window.location.replace('/');
          return;
        }
      } catch {
        // Hard failure: be safe and redirect
        window.location.replace('/');
      }
    })();
  }, []);
  useEffect(() => {
    async function refreshFromServer() {
      try {
        const data = await getSiteSettings();
        const p = data?.homepageSettings || {};
        
        // Load emailOTP settings from server if available and persist to localStorage
        if (p.emailOTP) {
          try { localStorage.setItem('emailOTPSettings', JSON.stringify(p.emailOTP)); } catch {}
        }
        
        setHomepageSettings((prev: any) => {
          const serverPricing = p.pricing || {};
          const serverPlans = Array.isArray(serverPricing?.plans) && serverPricing.plans.length > 0 ? serverPricing.plans : null;
          const prevPlans = (prev as any)?.pricing?.plans;
          let resolvedPlans: any[] = [];
          if (serverPlans) {
            resolvedPlans = serverPlans;
          } else if (Array.isArray(prevPlans) && prevPlans.length > 0) {
            resolvedPlans = prevPlans;
          } else {
            try {
              const raw = localStorage.getItem('server_plans_cache');
              if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr) && arr.length > 0) resolvedPlans = arr;
              }
            } catch {}
            if (resolvedPlans.length === 0) {
              resolvedPlans = Array.isArray(defaultHomepageSettings.pricing?.plans) ? defaultHomepageSettings.pricing.plans : [];
            }
          }
          const merged = { ...prev, ...p, pricing: { ...(serverPricing || {}), plans: resolvedPlans } };
          // Persist snapshot to enable full offline experience (not only pricing)
          try { localStorage.setItem('homepageSettings', JSON.stringify(merged)); } catch {}
          return merged;
        });
        return;
      } catch {}
    }
    function onStorage(e: StorageEvent) {
      if (e.key === 'homepageSettings' || e.key === 'homepageSettings_update_ts') {
        // Prefer fetching canonical server copy; fallback to localStorage payload
        refreshFromServer().then(() => {
          if (e.key === 'homepageSettings' && e.newValue) {
            try {
              const parsed = JSON.parse(e.newValue);
              setHomepageSettings((prev: any) => ({
                ...prev,
                ...parsed,
                pricing: (() => {
                  const parsedPricing = (parsed as any)?.pricing || {};
                  const parsedPlans = Array.isArray(parsedPricing?.plans) && parsedPricing.plans.length > 0 ? parsedPricing.plans : null;
                  if (parsedPlans) return { ...parsedPricing, plans: parsedPlans };
                  const prevPlans = (prev as any)?.pricing?.plans;
                  if (Array.isArray(prevPlans) && prevPlans.length > 0) return { ...parsedPricing, plans: prevPlans };
                  try {
                    const raw = localStorage.getItem('server_plans_cache');
                    if (raw) {
                      const arr = JSON.parse(raw);
                      if (Array.isArray(arr) && arr.length > 0) return { ...parsedPricing, plans: arr };
                    }
                  } catch {}
                  return { ...parsedPricing, plans: Array.isArray(defaultHomepageSettings.pricing?.plans) ? defaultHomepageSettings.pricing.plans : [] };
                })()
              }));
            } catch {}
          }
        });
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // load authentication settings
  useEffect(() => {
    try {
      const raw = localStorage.getItem('authenticationSettings');
      if (!raw) return;
      const parsed = JSON.parse(raw);
  setAuthenticationSettings(mergeAuthenticationSettings(parsed));
    } catch {}
  }, []);

  const handleSaveHomepageSettings = () => {
    try {
      localStorage.setItem('homepageSettings', JSON.stringify(homepageSettings));
      console.log('Homepage settings saved');
  // Inform the user once from the central save point
  safeToast('success', 'Homepage settings saved successfully!');
    } catch (err) {
      console.error('Error saving homepage settings', err);
    }
  };

  const handleNavigateToAuth = (type: 'admin' | 'user', opts?: { selectedPlan?: PaymentPlan }) => {
    if (type === 'user') {
      clearSignupSourceFlags();
      markDigitalCardSignupSource();
    }
    if (opts?.selectedPlan) {
      setSelectedPlan(opts.selectedPlan);
    } else {
      // If no plan is provided (free signup), clear any previously persisted paid plan
      setSelectedPlan(null);
    }
    setAuthType(type);
    setCurrentStep('auth');
  };

  const handleAdminLogin = (credentials: { email: string; password: string }, otpProof?: string) => {
    (async () => {
      try {
        const data = await adminLogin(credentials.email, credentials.password, otpProof);
        if (!data?.token) { safeToast('error', 'Login failed'); return; }
        const token = data.token || '';
        const role = data.role || null;

        clearNonAdminSessionTokens();
        localStorage.setItem('dev_admin_token', token);
        localStorage.setItem('dev_admin_role', role || '');
        dispatchOwnerTokenEvent();
  // store initial admin tab for immediate redirect
  const tab = role === 'basicadmin' ? 'basic' : 'overview';
  localStorage.setItem('dev_admin_initial_tab', tab);
  if (role === 'superadmin' || role === 'basicadmin') {
    try { localStorage.setItem('dev_admin_initial_mode', 'system_admin'); } catch {}
  } else if (!role || (role !== 'basicadmin' && role !== 'system_admin' && role !== 'superadmin')) {
    try { localStorage.setItem('dev_admin_initial_mode', 'card_owner'); } catch (e) {}
  }
        setIsAdminCreated(true);
        setIsAuthenticated(true);
        setAuthenticatedUser(null);
  // admin panel will read role from localStorage and set its internal tab on mount
  setCurrentStep('dashboard');
        safeToast('success', 'Logged in');
      } catch (err: any) {
        console.error('Admin login error', err);
        const code = String(err?.data?.error || err?.message || '');
        if (code === 'otp_required' || code === 'invalid_otp_proof') {
          safeToast('error', 'Two-factor authentication is required. Complete the verification step.');
        } else {
          safeToast('error', 'Login failed');
        }
      }
    })();
  };

  const handleUserSignup = (signupData: any) => {
  clearSignupSourceFlags();
  markDigitalCardSignupSource();
  // Persist to survive accidental refresh before TemplateSelection mounts
  try { localStorage.setItem('template_selection_user', JSON.stringify(signupData)); } catch {}
  setUserData(signupData);
    // Clear selectedPlan if user signed up for free (no paid plan selected at start)
    // This ensures template selection → dashboard flow without payment redirect
    if (!selectedPlan || (selectedPlan && Number(String(selectedPlan.price || '').replace(/[^0-9.]/g, '')) === 0)) {
      setSelectedPlan(null);
    }

    const pre = loadPreSignupTemplate();
    if (pre?.templateMetaId) {
      let meta: Record<string, any> | null = null;
      try {
        const cached = JSON.parse(localStorage.getItem('templates_cache_meta') || '{}');
        meta = cached[pre.templateMetaId] || null;
      } catch {}
      const payload = preSignupToSelectPayload(pre, meta as any);
      if (typeof payload === 'object' && meta) (payload as any).templateMeta = meta;
      clearPreSignupTemplate();
      handleTemplateSelect(payload as any, signupData);
      return;
    }
    setCurrentStep('template');
  };

  // Recover session after a hard refresh (admin first, then owner, then template selection)
  useEffect(() => {
    const cleanOwnerDashboardUrl = () => {
      try {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('dashboard');
        cleanUrl.searchParams.delete('panel');
        cleanUrl.searchParams.delete('target');
        cleanUrl.searchParams.delete('role');
        window.history.replaceState({}, '', cleanUrl.pathname + (cleanUrl.search || ''));
      } catch {}
    };

    const restoreOwnerDashboard = (sess: { id?: string | null; username?: string | null }) => {
      (async () => {
        let restored: any = null;
        let isNetworkError = false;
        try {
          const key = (sess.id || sess.username) as string;
          restored = await getUser(key);
        } catch (e: any) {
          const st = e?.status;
          if (!st || st >= 500 || st === 0) isNetworkError = true;
        }
        if (restored && restored.username) {
          const enriched = attachTemplateFallback(restored);
          setAuthType('user');
          setAuthenticatedUser(enriched as unknown as User);
          setIsAuthenticated(true);
          try { localStorage.setItem('dev_admin_initial_mode', 'card_owner'); } catch {}
          clearSignupSourceFlags();
          clearSignupTemp((enriched as any)?.email || null);
          if ((enriched as any).preferredLanguage) {
            const pl = normalizeUiLang((enriched as any).preferredLanguage);
            void i18n.changeLanguage(pl);
            persistManualLanguage(pl);
          }
          setCurrentStep('dashboard');
          cleanOwnerDashboardUrl();
        } else if (!isNetworkError) {
          try { localStorage.removeItem('owner_session'); } catch {}
          try { localStorage.removeItem('owner_token'); } catch {}
          dispatchOwnerTokenEvent();
        }
      })();
    };

    // Digital Card Panel deep-link: ?panel=owner&dashboard=1
    try {
      const usp = new URLSearchParams(window.location.search);
      const wantsDashboard = ['1','true','yes'].includes(String(usp.get('dashboard') || '').toLowerCase());
      const panelParam = (usp.get('panel') || usp.get('target') || usp.get('role') || '').toLowerCase();
      const ownerPanel = panelParam === 'owner' || panelParam === 'user' || panelParam === 'card-owner';
      if (wantsDashboard && ownerPanel) {
        const raw = localStorage.getItem('owner_session');
        if (raw) {
          const sess = JSON.parse(raw) as { id?: string | null; username?: string | null };
          if (sess && (sess.id || sess.username)) {
            restoreOwnerDashboard(sess);
            return;
          }
        }
      }
    } catch {}

    if (!isAuthenticated && !userData) {
      // -1) If a deep-link intent requested auth (e.g., from Leads unauthorized), honor it
      try {
        const intent = localStorage.getItem('auth_intent');
        // New: direct intents from support pages/footer to open auth immediately
        if (intent === 'admin_auth') {
          setAuthType('admin');
          setCurrentStep('auth');
          try { localStorage.removeItem('auth_intent'); } catch {}
          return;
        }
        if (intent === 'user_auth') {
          setAuthType('user');
          setCurrentStep('auth');
          try { localStorage.removeItem('auth_intent'); } catch {}
          return;
        }
        if (intent === 'owner_leads') {
          setAuthType('user');
          setCurrentStep('auth');
          // Keep initial hints for when dashboard mounts post-login
          try { localStorage.setItem('dev_admin_initial_mode', 'card_owner'); } catch {}
          try { localStorage.setItem('dev_admin_initial_tab', 'leads'); } catch {}
          // Consume intent so a hard refresh returns to HomePage
          try { localStorage.removeItem('auth_intent'); } catch {}
          // Do not run other recovery branches now
          return;
        } else if (intent === 'owner_ads') {
          setAuthType('user');
          setCurrentStep('auth');
          try { localStorage.setItem('dev_admin_initial_mode', 'card_owner'); } catch {}
          try { localStorage.setItem('card_owner_initial_tab', 'ads'); } catch {}
          try { localStorage.removeItem('auth_intent'); } catch {}
          return;
        }
      } catch {}

      // 0) If admin token exists, restore admin dashboard only if session is valid
      try {
        const token = localStorage.getItem('dev_admin_token');
        if (token) {
          (async () => {
            try {
              const sess = await serviceAdminSession(token);
              if (sess.ok) {
                const s = (sess.data || {}) as any;
                const role = s?.role || localStorage.getItem('dev_admin_role') || null;
                // Seed role and initial tab/mode hints for AdminDashboard to consume
                try { localStorage.setItem('dev_admin_role', role || ''); } catch {}
                try { localStorage.setItem('dev_admin_initial_tab', role === 'basicadmin' ? 'basic' : 'overview'); } catch {}
                try { localStorage.setItem('dev_admin_initial_mode', 'system_admin'); } catch {}
                // Only now, with a valid session, mark as authenticated admin
                setAuthType('admin');
                setIsAdminCreated(true);
                setIsAuthenticated(true);
                setAuthenticatedUser(null);
                setCurrentStep('dashboard');
              } else {
                // Invalid/expired session: clear any stale admin tokens/roles
                try { localStorage.removeItem('dev_admin_token'); } catch {}
                try { localStorage.removeItem('dev_admin_role'); } catch {}
                try { localStorage.removeItem('dev_admin_initial_tab'); } catch {}
                try { localStorage.removeItem('dev_admin_initial_mode'); } catch {}
                dispatchOwnerTokenEvent();
              }
            } catch {
              // Network failure: do not silently elevate to admin in production
              // Only in development would we allow local fallback
              if (import.meta && (import.meta as any).env && (import.meta as any).env.DEV) {
                try { localStorage.setItem('dev_admin_initial_tab', 'overview'); } catch {}
                try { localStorage.setItem('dev_admin_initial_mode', 'system_admin'); } catch {}
                setAuthType('admin');
                setIsAdminCreated(true);
                setIsAuthenticated(true);
                setAuthenticatedUser(null);
                setCurrentStep('dashboard');
              }
            }
          })();
          return; // Stop further recovery branches
        }
      } catch {}

      // 1) If owner session exists, restore directly to dashboard
      try {
        const raw = localStorage.getItem('owner_session');
        if (raw) {
          const sess = JSON.parse(raw) as { id?: string | null; username?: string | null };
          if (sess && (sess.id || sess.username)) {
            restoreOwnerDashboard(sess);
            return; // don't run other recovery branches
          }
        }
      } catch {}

      // 2) Else, try to recover template selection user (digital-card signup only)
      try {
        if (isMiniwebsiteSignupComplete()) return;
        if (localStorage.getItem('owner_token')) return;
        const raw = localStorage.getItem('template_selection_user');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            setUserData(parsed);
            setCurrentStep('template');
          }
        }
      } catch {}
    }
    // Only run once on mount for recovery
     
  }, []);

  // Clear temporary signup/template-selection state so a new signup starts clean
  const clearSignupTemp = (email?: string | null) => {
    try {
      localStorage.removeItem('signup_progress_v1');
      localStorage.removeItem('template_selection_user');
      clearSignupSourceFlags();
      if (email) {
        const e = String(email).toLowerCase();
        localStorage.removeItem(`template_selection:${e}`);
        localStorage.removeItem(`template_selection_category:${e}`);
      }
    } catch {}
  };

  const rememberTemplateFallback = (email?: string | null, fallbackId?: string | null) => {
    if (!email || !fallbackId) return;
    const key = `template_fallback:${String(email).trim().toLowerCase()}`;
    if (!key.endsWith(':')) {
      try { localStorage.setItem(key, fallbackId); } catch {}
    }
  };

  const attachTemplateFallback = <T extends Record<string, any>>(user: T | null | undefined): T | null | undefined => {
    if (!user) return user;
    try {
      const email = String((user as any).email || '').trim().toLowerCase();
      if (!email) return user;
      const stored = localStorage.getItem(`template_fallback:${email}`);
      if (stored && ['1','2','3','4','5'].includes(stored)) {
        const hasMeta = !!String((user as any).templateMetaId || '').trim();
        const classicId = String((user as any).templateId || '').trim();
        if (!hasMeta && !['1','2','3','4','5'].includes(classicId)) {
          return { ...(user as any), templateId: stored, templateClassicFallbackId: stored } as T;
        }
        return { ...(user as any), templateClassicFallbackId: stored } as T;
      }
    } catch {}
    return user;
  };

  const handleUserSignin = (credentials: { email: string; password: string }) => {
    (async () => {
      try { localStorage.removeItem('owner_token'); localStorage.removeItem('owner_session'); } catch {}
      dispatchOwnerTokenEvent();
      let success = false;
      try {
        const data = await userLogin(credentials.email, credentials.password);
        if (data?.token) {
          try { localStorage.setItem('owner_token', data.token); } catch {}
          dispatchOwnerTokenEvent();
          try {
            const u = data.user || {};
            const sess = { id: u?.id || u?._id || null, username: u?.username || null };
            localStorage.setItem('owner_session', JSON.stringify(sess));
          } catch {}
          const userKey = data?.user?.id || data?.user?._id || data?.user?.username;
          const serverUser = userKey ? await getUser(String(userKey)) : (data?.user || null);
          if (serverUser) {
            const enriched = attachTemplateFallback(serverUser) as any;
            setAuthenticatedUser(enriched as any);
            if ((enriched as any).preferredLanguage) {
              const pl = normalizeUiLang((enriched as any).preferredLanguage);
              void i18n.changeLanguage(pl);
              persistManualLanguage(pl);
            }
            success = true;
          }
          // Flush any pending template selection saved pre-auth
          try {
            const pendingKey = `pending_template_patch:${String(credentials.email || '').toLowerCase()}`;
            const raw = localStorage.getItem(pendingKey);
            if (raw) {
              const patch = JSON.parse(raw || '{}') as any;
              // Resolve id target (prefer id/_id over username)
              const idOrUsername = (serverUser as any)?.id || (serverUser as any)?._id || (serverUser as any)?.username;
              if (idOrUsername) {
                let target = String(idOrUsername);
                try {
                  if (!/^[0-9a-fA-F]{24}$/.test(target)) {
                    const rec = await getUser(String(idOrUsername)).catch(() => null as any);
                    const resolved = (rec as any)?._id || (rec as any)?.id || null;
                    if (resolved && /^[0-9a-fA-F]{24}$/.test(String(resolved))) target = String(resolved);
                  }
                } catch {}
                try { await updateUser(target, patch); } catch {}
                try { localStorage.removeItem(pendingKey); } catch {}
                if ((import.meta as any).env?.DEV) {
                  try { console.debug('[template-select] flushed pending patch after sign-in', { pendingKey }); } catch {}
                }
              }
            }
          } catch {}
        } else {
          // dev fallback
          const DEV_FALLBACK = localStorage.getItem('enable_dev_login_fallback') === '1';
          if (DEV_FALLBACK) {
            try {
              const raw = localStorage.getItem('users') || localStorage.getItem('dev_users');
              if (raw) {
                const arr = JSON.parse(raw) as any[];
                const found = arr.find(u => (u.email || '').toLowerCase() === credentials.email.toLowerCase());
                if (found) {
                  const enriched = attachTemplateFallback(found) as any;
                  setAuthenticatedUser(enriched as any);
                  try { localStorage.setItem('owner_session', JSON.stringify({ id: found.id || null, username: found.username || null })); } catch {}
                  success = true;
                }
              }
            } catch {}
          }
        }
      } catch {
        const DEV_FALLBACK = localStorage.getItem('enable_dev_login_fallback') === '1';
        if (DEV_FALLBACK) {
          try {
            const raw = localStorage.getItem('users') || localStorage.getItem('dev_users');
            if (raw) {
              const arr = JSON.parse(raw) as any[];
              const found = arr.find(u => (u.email || '').toLowerCase() === credentials.email.toLowerCase());
                if (found) {
                  const enriched = attachTemplateFallback(found) as any;
                  setAuthenticatedUser(enriched as any);
                try { localStorage.setItem('owner_session', JSON.stringify({ id: found.id || null, username: found.username || null })); } catch {}
                success = true;
              }
            }
          } catch {}
        }
      }
      if (success) {
        setIsAuthenticated(true);
        try {
          const intent = localStorage.getItem('auth_intent');
          if (intent === 'owner_leads') {
            localStorage.setItem('dev_admin_initial_mode', 'card_owner');
            localStorage.setItem('dev_admin_initial_tab', 'leads');
            localStorage.removeItem('auth_intent');
          } else if (intent === 'owner_ads') {
            localStorage.setItem('dev_admin_initial_mode', 'card_owner');
            localStorage.setItem('card_owner_initial_tab', 'ads');
            localStorage.removeItem('auth_intent');
          } else {
            localStorage.setItem('dev_admin_initial_mode', 'card_owner');
          }
          clearSignupTemp(credentials.email);
        } catch {}
        setCurrentStep('dashboard');
      } else {
        safeToast('error', 'Invalid email or password');
        setIsAuthenticated(false);
        setAuthenticatedUser(null);
      }
    })();
  };

  const handleTemplateSelect = (payload: { templateId?: string; templateMetaId?: string; templateMeta?: Record<string, any>; componentKey?: string; variant?: string } | string, userOverride?: any) => {
    const ud = userOverride || userData;
    if (!ud) return;
    const hasMeta = typeof payload !== 'string' && !!payload?.templateMetaId;
    // When selecting a dynamic template (meta present), clear classic templateId (set to null)
    const templateId = hasMeta ? (null as any) : (typeof payload === 'string' ? payload : (payload?.templateId || '1'));
    const templateMetaId = typeof payload === 'string' ? undefined : payload?.templateMetaId;
    const templateMeta = typeof payload === 'string' ? undefined : payload?.templateMeta;
    
    // Map classic template IDs to their templateKey for preview compatibility
    const templateIdToKeyMap: Record<string, string> = {
      '1': 'professional-classic',
      '2': 'elegant-looking',
      '3': 'modern-clean',
      '4': 'golden-view',
      '5': 'classic-modular'
    };
    // Also accept componentKey directly from dynamic template meta (e.g., 'elegant-looking')
    const componentKeyFromPayload = typeof payload !== 'string' ? (payload?.componentKey || (payload?.templateMeta as any)?.componentKey) : undefined;
    const knownClassicKeys = ['professional-classic', 'elegant-looking', 'modern-clean', 'golden-view', 'classic-modular'];
    // Derive templateKey: prefer componentKey if it matches a classic variant, else map from templateId
    const templateKey = (() => {
      // 1) If componentKey is a known classic variant, use it directly
      if (componentKeyFromPayload && knownClassicKeys.includes(String(componentKeyFromPayload).toLowerCase())) {
        return String(componentKeyFromPayload).toLowerCase();
      }
      // 2) Otherwise map from templateId if available
      if (templateId && templateIdToKeyMap[templateId]) {
        return templateIdToKeyMap[templateId];
      }
      // 3) No mapping found
      return undefined;
    })();
    
    if (templateMeta && templateMetaId) {
      const normalizedMeta = { ...(templateMeta as any), id: templateMetaId };
      // Cache dynamic template spec immediately so owner preview can render without refetching
      try {
        const cacheKey = 'templates_cache_meta';
        const raw = localStorage.getItem(cacheKey);
        const existing = raw ? JSON.parse(raw) : {};
        localStorage.setItem(cacheKey, JSON.stringify({ ...existing, [templateMetaId]: normalizedMeta }));
      } catch {}
      try {
        (window as any).__TEMPLATES_META_BY_ID__ = {
          ...(window as any).__TEMPLATES_META_BY_ID__,
          [templateMetaId]: normalizedMeta,
        };
      } catch {}
    }
    // Map templateKey back to classic ID for fallback purposes
    const templateKeyToIdMap: Record<string, string> = {
      'professional-classic': '1',
      'elegant-looking': '2',
      'modern-clean': '3',
      'golden-view': '4',
      'classic-modular': '5'
    };
    const fallbackTemplateId = (() => {
      if (typeof payload === 'string') {
        return /^[1-5]$/.test(payload) ? payload : null;
      }
      if (payload?.templateId && /^[1-5]$/.test(String(payload.templateId))) {
        return String(payload.templateId);
      }
      // If we have a valid templateKey, derive the classic ID from it
      if (templateKey && templateKeyToIdMap[templateKey]) {
        return templateKeyToIdMap[templateKey];
      }
      return null;
    })();
    if (fallbackTemplateId) {
      rememberTemplateFallback((ud as any)?.email, fallbackTemplateId);
    }

    // Ensure we have a valid templateId for storage - use fallbackTemplateId if templateId is null
    const effectiveTemplateId = templateId || fallbackTemplateId;

    // 1) Optimistic: persist selection locally and navigate immediately
    try {
      const raw = localStorage.getItem('template_selection_user');
      const local = raw ? JSON.parse(raw) : (ud || {});
      const updated = { ...(local || {}), templateId: effectiveTemplateId, ...(templateKey ? { templateKey } : {}), ...(templateMetaId ? { templateMetaId } : { templateMetaId: null }), ...(fallbackTemplateId ? { templateClassicFallbackId: fallbackTemplateId } : {}) };
      localStorage.setItem('template_selection_user', JSON.stringify(updated));
    } catch {}
    setAuthenticatedUser({ ...(ud as any), templateId: effectiveTemplateId, ...(templateKey ? { templateKey } : {}), ...(templateMetaId ? { templateMetaId } : { templateMetaId: null }), ...(fallbackTemplateId ? { templateClassicFallbackId: fallbackTemplateId } : {}) } as any);
  setIsAuthenticated(true);
  // If a paid plan was selected (numeric amount > 0), go to payment step; otherwise go to dashboard
  const planAmount = (() => {
    try {
      const s = String(selectedPlan?.price || '').trim();
      // Extract numeric amount regardless of currency symbol/code
      const n = Number(s.replace(/[^0-9.]/g, '')) || 0;
      return n;
    } catch {
      return 0;
    }
  })();
  const planLooksPaid = (planAmount > 0);
  // If no plan chosen yet, require plan selection next
  if (!selectedPlan) {
    setCurrentStep('plan');
  } else {
    // Normal digital-card signup always opens the card owner panel (never mini-website dashboard)
    setCurrentStep(planLooksPaid ? 'payment' : 'dashboard');
  }

    // 2) Background save to server; refresh and finalize session details when ready
    (async () => {
      try {
        // If we are not authenticated yet, skip network calls and queue a pending patch
        let hasOwner = false;
        let hasAdmin = false;
        try {
          hasOwner = !!localStorage.getItem('owner_token');
          hasAdmin = !!localStorage.getItem('dev_admin_token');
        } catch {}
        if (!hasOwner && !hasAdmin) {
          try {
            const email = String((ud as any)?.email || '').trim().toLowerCase();
            if (email) {
              const pendingKey = `pending_template_patch:${email}`;
              const pending = { templateId: effectiveTemplateId, templateKey, templateMetaId: templateMetaId || null } as any;
              localStorage.setItem(pendingKey, JSON.stringify(pending));
              if ((import.meta as any).env?.DEV) {
                try { console.debug('[template-select] queued pending patch (unauth)', { pendingKey, ...pending }); } catch {}
              }
            }
          } catch {}
          return; // defer save until after sign-in
        }
        const idOrUsername = (ud.id || ud._id || (ud as any).username) as string | undefined;
        if (idOrUsername) {
          // Resolve username to ObjectId to avoid 404 on PATCH when server expects id
          let target = String(idOrUsername);
          try {
            if (!/^[0-9a-fA-F]{24}$/.test(target)) {
              const rec = await getUser(String(idOrUsername)).catch(() => null as any);
              const resolved = (rec as any)?._id || (rec as any)?.id || null;
              if (resolved && /^[0-9a-fA-F]{24}$/.test(String(resolved))) target = String(resolved);
            }
          } catch {}
          // Save template selection
          const patch: any = { templateId: effectiveTemplateId, templateKey, templateMetaId: templateMetaId || null };
          await updateUser(target, patch);
          // Fetch fresh user
          const fresh = await getUser(target);
          const merged = { ...(ud as any), ...(fresh as any) } as User;
          if (fallbackTemplateId) (merged as any).templateClassicFallbackId = fallbackTemplateId;
          const enrichedMerged = fallbackTemplateId ? merged : (attachTemplateFallback(merged) as User);
          setAuthenticatedUser(enrichedMerged);
          try {
            localStorage.setItem('owner_session', JSON.stringify({ id: (merged as any).id || (merged as any)._id || null, username: (merged as any).username || null }));
            const intent = localStorage.getItem('auth_intent');
            if (intent === 'owner_leads') {
              localStorage.setItem('dev_admin_initial_mode', 'card_owner');
              localStorage.setItem('dev_admin_initial_tab', 'leads');
              localStorage.removeItem('auth_intent');
            } else if (intent === 'owner_ads') {
              localStorage.setItem('dev_admin_initial_mode', 'card_owner');
              localStorage.setItem('card_owner_initial_tab', 'ads');
              localStorage.removeItem('auth_intent');
            } else {
              localStorage.setItem('dev_admin_initial_mode', 'card_owner');
            }
            clearSignupTemp((ud as any)?.email || (fresh as any)?.email || null);
          } catch {}
        }
      } catch (e) {
        // ignore; user is already on dashboard; retry flows can happen later
        try { console.warn('[template-select] background save failed'); } catch {}
      }
    })();
  };

  if (currentStep === 'home') {
  if ((homepageSettings as any)?.maintenance?.enabled) {
    return (
      <MaintenancePage
        settings={(homepageSettings as any).maintenance}
        branding={(homepageSettings as any).branding}
        onNavigateToAuth={handleNavigateToAuth}
      />
    );
  }
  return <HomePage onNavigateToAuth={handleNavigateToAuth} customSettings={homepageSettings} />;
  }

  if (currentStep === 'plan' && isAuthenticated) {
    const plans = (homepageSettings as any)?.pricing?.plans || [];
    const onChoose = (plan: any) => {
      try { setSelectedPlan(plan); localStorage.setItem('selectedPlan', JSON.stringify(plan)); } catch {}
      const amt = Number(String(plan?.price || '').replace(/[^0-9.]/g, '')) || 0;
      setCurrentStep(amt > 0 ? 'payment' : 'dashboard');
    };
    return (
      <PlanSelectionStep
        plans={plans}
        onBack={() => setCurrentStep('template')}
        onChoose={onChoose}
      />
    );
  }

  if (currentStep === 'auth') {
    return (
      <AuthPage
        authType={authType}
        initialView={authInitialView}
        onBack={() => {
          const backUrl = getAuthBackHomeUrl();
          if (backUrl !== '/') {
            navigate(backUrl);
            return;
          }
          setCurrentStep('home');
        }}
        onAdminLogin={handleAdminLogin}
        onUserSignup={handleUserSignup}
  onUserSignin={handleUserSignin}
  isAdminCreated={isAdminCreated}
  authSettings={authenticationSettings}
      />
    );
  }

  if (currentStep === 'template' && userData) {
    return (
      <TemplateSelection
        userData={userData}
        onTemplateSelect={handleTemplateSelect}
        onBack={() => setCurrentStep('auth')}
      />
    );
  }

  if (currentStep === 'payment' && isAuthenticated) {
    const u = authenticatedUser || userData;
    const plan = selectedPlan;
    return (
      <div className="p-4 max-w-6xl mx-auto">
        <button className="mb-4 text-sm text-blue-600" onClick={() => setCurrentStep('template')}>{t('auth_plan_back_templates')}</button>
        {plan ? (
            <PaymentPanel
            user={{ id: (u as any)?.id || (u as any)?._id, name: (u as any)?.name, email: (u as any)?.email, phone: (u as any)?.phone, position: (u as any)?.position, company: (u as any)?.company, location: (u as any)?.location }}
            plan={plan}
            onSuccess={async () => {
              // Refresh user from server so dashboard reflects activated subscription
              try {
                const key = (u as any)?.id || (u as any)?._id || (u as any)?.username;
                if (key) {
                  const fresh = await getUser(String(key));
                  if (fresh) {
                    const enriched = attachTemplateFallback(fresh) as any;
                    setAuthenticatedUser(enriched as any);
                  }
                }
              } catch {}
              // Clear selected plan so a new session starts clean
              try { setSelectedPlan(null); localStorage.removeItem('selectedPlan'); } catch {}
              setCurrentStep('dashboard');
            }}
            onCancel={() => { setCurrentStep('template'); }}
            onBusyChange={(busy: boolean) => { try { setCheckoutBusy(busy); } catch {} }}
          />
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl">No plan selected. Returning to dashboard…</div>
        )}
      </div>
    );
  }

  if (currentStep === 'dashboard' && isAuthenticated) {
    return (
      <AdminDashboard
        authenticatedUser={authenticatedUser}
        authType={authType}
        homepageSettings={homepageSettings}
  onUpdateHomepageSettings={(updates) => setHomepageSettings((prev: any) => ({ ...prev, ...updates }))}
        onSaveHomepageSettings={handleSaveHomepageSettings}
        onLogout={() => {
          // reset auth flow to home (clean)
          setIsAuthenticated(false);
          setAuthenticatedUser(null);
          try { localStorage.removeItem('owner_session'); } catch {}
          try { localStorage.removeItem('owner_token'); } catch {}
          dispatchOwnerTokenEvent();
          // Go to root so HomeRoute applies maintenance guard
          try { window.location.assign('/'); } catch { setCurrentStep('home'); }
        }}
      />
    );
  }

  // fallback
  return <HomePage onNavigateToAuth={handleNavigateToAuth} customSettings={homepageSettings} />;
};

/* ---------- Admin Dashboard (cleaned) ---------- */
interface AdminDashboardProps {
  authenticatedUser?: User | null;
  authType: 'admin' | 'user';
  homepageSettings: any;
  onUpdateHomepageSettings: (updates: any) => void;
  onSaveHomepageSettings: () => void;
  onLogout?: () => void;
  initialAdminTab?: string;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({
  authenticatedUser,
  authType,
  homepageSettings: parentHomepageSettings,
  onUpdateHomepageSettings: parentUpdateHomepageSettings,
  onSaveHomepageSettings: parentSaveHomepageSettings,
  onLogout
}) => {
  const { t } = useTranslation();
  // React Router location must be used at the top level (Rules of Hooks)
  const location = useLocation();
  const path = location?.pathname ?? '';
  // single source of truth states
  // Include any locally created dev users (saved under localStorage.dev_users) so
  // newly-created users appear immediately in the admin dashboard when running
  // in dev mode (no backend available).
  const DISABLE_MOCK_USERS = (import.meta as any)?.env?.VITE_DISABLE_MOCK_USERS === '1';
  const CLEAR_USER_CACHE_ON_BOOT = (import.meta as any)?.env?.VITE_CLEAR_USER_CACHE_ON_BOOT === '1';

  // Optional one-time cache clear on boot (controlled by env)
  React.useEffect(() => {
    if (CLEAR_USER_CACHE_ON_BOOT) {
      try { localStorage.removeItem('users'); } catch {}
      try { localStorage.removeItem('dev_users'); } catch {}
      try { localStorage.removeItem('dev_users_last_created'); } catch {}
    }
     
  }, []);

  const initialUsers: User[] = (() => {
    try {
      const raw = localStorage.getItem('users');
      const persisted = raw ? JSON.parse(raw) as User[] : [];
      const rawDev = localStorage.getItem('dev_users');
      const dev = rawDev ? JSON.parse(rawDev) as User[] : [];
      if (DISABLE_MOCK_USERS) {
        // Strict mode: show only persisted users, no dev or mock fallbacks
        return persisted;
      }
      // Default: prioritize persisted users, then dev fallbacks, else mock users
      const merged = [...persisted, ...dev];
      return merged.length ? merged : (mockUsers as any);
    } catch (e) {
      return DISABLE_MOCK_USERS ? [] : (mockUsers as any);
    }
  })();

  const [users, setUsers] = useState<User[]>(initialUsers);
  const [ads, setAds] = useState<AdConfig[]>([...mockAds] as any);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsUnauthorized, setLeadsUnauthorized] = useState(false);
  const [ownerAuthVersion, setOwnerAuthVersion] = useState(0);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleOwnerTokenEvent = () => {
      setOwnerAuthVersion((prev) => prev + 1);
    };
    const handleStorage = (e: StorageEvent) => {
      try {
        if (e && (e.key === 'owner_token' || e.key === 'dev_admin_token')) {
          setOwnerAuthVersion((prev) => prev + 1);
        }
      } catch {}
    };
    window.addEventListener(OWNER_TOKEN_EVENT, handleOwnerTokenEvent);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(OWNER_TOKEN_EVENT, handleOwnerTokenEvent);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);
  const [templatesMetaById, setTemplatesMetaById] = React.useState<Record<string, any>>(() => {
    try {
      const raw = localStorage.getItem('templates_cache_meta');
      if (raw) return JSON.parse(raw) as Record<string, any>;
    } catch {}
    return {} as Record<string, any>;
  });
  const mergeTemplateMetaCache = React.useCallback((map: Record<string, any>) => {
    if (!map) return;
    const normalized: Record<string, any> = {};
    Object.entries(map).forEach(([key, value]) => {
      if (!value) return;
      const id = String(key || (value as any)?.id || '').trim();
      if (!id) return;
      normalized[id] = { ...(value as any), id };
    });
    if (!Object.keys(normalized).length) return;
    setTemplatesMetaById((prev) => ({ ...prev, ...normalized }));
    try {
      (window as any).__TEMPLATES_META_BY_ID__ = {
        ...(window as any).__TEMPLATES_META_BY_ID__,
        ...normalized,
      };
    } catch {}
    try {
      const cacheKey = 'templates_cache_meta';
      const currentRaw = localStorage.getItem(cacheKey);
      const existing = currentRaw ? JSON.parse(currentRaw) : {};
      localStorage.setItem(cacheKey, JSON.stringify({ ...existing, ...normalized }));
    } catch {}
  }, []);
  const [homepageSettings, setHomepageSettings] = useState<HomepageSettings>(() =>
    mergeHomepageSettings(parentHomepageSettings || defaultHomepageSettings),
  );
  const [paymentGateways, setPaymentGateways] = useState<PaymentGateway[]>([...mockPaymentGateways] as any);
  // Toggle for Homepage live preview panel (renders public homepage with current settings)
  // Note: state may already exist; rely on existing declaration if present.
  // Ensure adminSession is declared before any hooks that reference it in their dependency arrays
  const [adminSession, setAdminSession] = useState<{ id?: string; email?: string | null; role?: string | null } | null>(null);
  // Hydrate payment gateways from server payment-settings (canonical) once admin session is known
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only attempt if admin token exists (basic or super) – otherwise keep public mock view
      const token = localStorage.getItem('dev_admin_token');
      if (!token) return;
      try {
        const { paymentSettings } = await getPaymentSettings();
        const gw = paymentSettingsToGateways(paymentSettings);
        if (!cancelled && gw && gw.length) setPaymentGateways(gw);
      } catch (e) {
        // fallback silently; keep mockPaymentGateways
        try { console.warn('[App] failed to hydrate payment gateways from server', (e as any)?.message); } catch {}
      }
    })();
    return () => { cancelled = true; };
  }, [adminSession?.role]);
  // Listen for cross-tab refresh signal after save
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === 'payment_settings_refresh' && e.newValue) {
        (async () => {
          try {
            const { paymentSettings } = await getPaymentSettings();
            const gw = paymentSettingsToGateways(paymentSettings);
            if (gw && gw.length) setPaymentGateways(gw);
          } catch {}
        })();
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const [emailGateways, setEmailGateways] = useState<any[]>([...mockEmailGateways]);
  const [smsGateways, setSmsGateways] = useState<any[]>([...mockSMSGateways]);
  const [systemStats, setSystemStats] = useState<any>(() => {
    try {
      const raw = localStorage.getItem('systemStats');
      return raw ? JSON.parse(raw) : mockSystemStats;
    } catch {
      return mockSystemStats;
    }
  });
  // If local cached stats look clearly stale (all core totals zero), replace with mock defaults
  // This avoids indefinitely showing a stale zero snapshot when the initial fetch fails.
  useEffect(() => {
    try {
      const s = systemStats as any;
      const looksZero = s && Number(s.totalUsers || 0) === 0 && Number(s.totalViews || 0) === 0 && Number(s.totalAdViews || 0) === 0;
      if (looksZero) {
        setSystemStats(mockSystemStats);
        try { localStorage.setItem('systemStats', JSON.stringify(mockSystemStats)); } catch {}
      }
    } catch {}
  }, []);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>(() => {
    try {
      const raw = localStorage.getItem('subscriptionPlans');
      return raw ? JSON.parse(raw) as SubscriptionPlan[] : mockSubscriptionPlans;
    } catch {
      return mockSubscriptionPlans;
    }
  });
  const [captchaSettings, setCaptchaSettings] = useState(defaultCaptchaSettings);
  const [emailOTPSettings, setEmailOTPSettings] = useState<EmailOTPSettings>(defaultEmailOTPSettings as any);

  // Load emailOTP settings from localStorage (populated by server on app init)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('emailOTPSettings');
      if (stored) {
        const parsed = JSON.parse(stored);
        setEmailOTPSettings(parsed);
      }
    } catch (err) {
      console.error('Error loading emailOTP settings from localStorage', err);
    }
  }, []);

  // UI state
  // Removed unused legacy state for old homepage customization panel
  const [_authSettings, _setAuthSettings] = useState(defaultAuthenticationSettings);
  const [showingHomepagePreview, setShowingHomepagePreview] = useState(false);
  const [editingSubscriptionPlans, setEditingSubscriptionPlans] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [subRules, setSubRules] = useState<AdminSubscriptionRules>({});
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesSaving, setRulesSaving] = useState(false);
  const [rulesAudit, setRulesAudit] = useState<{ id?: string; email?: string; role?: string; at?: string } | null>(null);

  const [adminMode, setAdminMode] = useState<'none' | 'card_owner' | 'system_admin'>(() => {
    try {
      const m = localStorage.getItem('dev_admin_initial_mode');
      if (m) {
        // consume the hint immediately so it's not reused later
        localStorage.removeItem('dev_admin_initial_mode');
        return m as any;
      }
    } catch (e) {
      // ignore
    }
    return authType === 'admin' ? 'system_admin' : 'card_owner';
  });
  const [adminActiveTab, setAdminActiveTab] = useState('overview');
  // Header notification bell counts
  const [headerNotifTotal, setHeaderNotifTotal] = useState(0);
  const [headerPendingWd, setHeaderPendingWd] = useState(0);
  const [headerUnattendedWd, setHeaderUnattendedWd] = useState(0);
  const [headerPendingTpls, setHeaderPendingTpls] = useState(0);
  const [headerPendingAds, setHeaderPendingAds] = useState(0);
  const [bellDropdownOpen, setBellDropdownOpen] = useState(false);
  const bellRef = React.useRef<HTMLDivElement>(null);
  const [dismissedNotifs, setDismissedNotifs] = useState<Record<string, number>>(() => {
    try { return JSON.parse(localStorage.getItem('notif_dismissed') || '{}'); } catch { return {}; }
  });
  const [readNotifs, setReadNotifs] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('notif_read') || '[]')); } catch { return new Set(); }
  });
  // Track requested tab for CardOwnerPanel (e.g. CRM button in header)
  const [ownerRequestedTab, setOwnerRequestedTab] = useState<string | null>(null);
  const [crmHeaderAccess, setCrmHeaderAccess] = useState<{ show: boolean; active: boolean }>({ show: false, active: false });
  // When an admin chooses to "View as Card Owner", this tracks the target owner's user id
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);

  // Fetch header notification counts periodically
  React.useEffect(() => {
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const token = typeof window !== 'undefined' ? (localStorage.getItem('dev_admin_token') || '') : '';
        if (!token) return;
        let total = 0;
        // Pending withdrawals
        try {
          const wdRes = await fetch('/api/websites/admin/withdrawals?status=pending', {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (wdRes.ok) {
            const wdData = await wdRes.json();
            if (!cancelled) {
              setHeaderPendingWd(wdData.pendingCount || 0);
              setHeaderUnattendedWd(wdData.unattendedCount || 0);
              total += (wdData.pendingCount || 0) + (wdData.unattendedCount || 0);
            }
          }
        } catch {}
        // Pending templates/ads
        try {
          const [tplRes, adRes] = await Promise.all([
            fetch('/api/templates', { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
            fetch('/api/admin/ads', { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          ]);
          const tpls = tplRes?.ok ? await tplRes.json().catch(() => []) : [];
          const ads = adRes?.ok ? await adRes.json().catch(() => []) : [];
          const pendingTpls = (Array.isArray(tpls) ? tpls : []).filter((t: any) => (t.approvalStatus || 'pending') === 'pending').length;
          const pendingAds = (Array.isArray(ads) ? ads : []).filter((a: any) => (a.approvalStatus || 'pending') === 'pending').length;
          if (!cancelled) { setHeaderPendingTpls(pendingTpls); setHeaderPendingAds(pendingAds); }
          total += pendingTpls + pendingAds;
        } catch {}
        if (!cancelled) setHeaderNotifTotal(total);
      } catch {}
    };
    fetchCounts();
    const iv = window.setInterval(fetchCounts, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [adminSession]);

  // Close bell dropdown on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setBellDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Build notification items for the bell dropdown
  const notifItems = React.useMemo(() => {
    const items: { key: string; severity: 'critical'|'warning'|'info'; icon: string; title: string; desc: string; count: number; tab: string }[] = [];
    if (headerUnattendedWd > 0) items.push({ key: 'wd-unattended', severity: 'critical', icon: '🚨', title: 'Unattended Withdrawals', desc: `${headerUnattendedWd} pending 72h+ without response`, count: headerUnattendedWd, tab: 'mini-websites' });
    if (headerPendingWd > 0) items.push({ key: 'wd-pending', severity: 'warning', icon: '💰', title: 'Pending Withdrawals', desc: 'Awaiting payout processing', count: headerPendingWd, tab: 'mini-websites' });
    if (headerPendingTpls > 0) items.push({ key: 'tpl-pending', severity: 'info', icon: '📋', title: 'Pending Templates', desc: 'Templates awaiting approval', count: headerPendingTpls, tab: 'templates' });
    if (headerPendingAds > 0) items.push({ key: 'ad-pending', severity: 'info', icon: '📢', title: 'Pending Advertisements', desc: 'Ads awaiting approval', count: headerPendingAds, tab: 'ads' });
    return items;
  }, [headerUnattendedWd, headerPendingWd, headerPendingTpls, headerPendingAds]);

  const visibleNotifItems = notifItems.filter(item => dismissedNotifs[item.key] === item.count ? false : true);
  const badgeCount = visibleNotifItems.reduce((s, i) => s + i.count, 0);

  const dismissNotif = (key: string, count: number) => {
    setDismissedNotifs(prev => {
      const next = { ...prev, [key]: count };
      try { localStorage.setItem('notif_dismissed', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const dismissAllNotifs = () => {
    const next: Record<string, number> = {};
    notifItems.forEach(i => { next[i.key] = i.count; });
    setDismissedNotifs(next);
    try { localStorage.setItem('notif_dismissed', JSON.stringify(next)); } catch {}
  };
  const markRead = (key: string) => {
    setReadNotifs(prev => {
      const next = new Set(prev);
      next.add(key);
      try { localStorage.setItem('notif_read', JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const markAllRead = () => {
    const next = new Set(notifItems.map(i => i.key));
    setReadNotifs(next);
    try { localStorage.setItem('notif_read', JSON.stringify([...next])); } catch {}
  };

  // Compute effective role from server session or localStorage dev override so UI reflects role immediately
  const effectiveAdminRole = adminSession?.role || (() => {
    try { return localStorage.getItem('dev_admin_role') || null; } catch { return null; }
  })();

  // Use an explicit initial tab saved at login/session time
  React.useEffect(() => {
    try {
      const tab = localStorage.getItem('dev_admin_initial_tab');
      if (tab) {
        setAdminActiveTab(tab);
        // clear the initial hint so it's only used once
        localStorage.removeItem('dev_admin_initial_tab');
      }
      // initial mode (card_owner vs system_admin) can be supplied at login time
      const mode = localStorage.getItem('dev_admin_initial_mode');
      if (mode) {
        setAdminMode(mode as any);
        localStorage.removeItem('dev_admin_initial_mode');
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // load admin session (dev) if token present
  useEffect(() => {
    const token = localStorage.getItem('dev_admin_token');
    if (!token) return;
    (async () => {
      try {
  const sess = await serviceAdminSession(token);
        if (!sess.ok) return;
        const s = (sess.data || {}) as any;
        setAdminSession(s || null);
        if (s && s.role) {
          const tab = s.role === 'basicadmin' ? 'basic' : 'overview';
          setAdminActiveTab(tab);
          try { localStorage.setItem('dev_admin_role', s.role); } catch {}
        }
      } catch (err) {
        // ignore
      }
    })();
  }, []);

  // Load ads and system stats from server (override initial mocked state)
  useEffect(() => {
    (async () => {
      let localAdViewsSum = 0;
      try {
        const token = localStorage.getItem('dev_admin_token');
        // Admins see full list; public sees only active ads
        const adsUrl = token ? '/api/admin/ads' : '/api/ads';
        const res = await fetch(adsUrl, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length) {
            const normalized = (data as any[]).map((a) => ({
              ...a,
              id: String((a as any).id ?? (a as any)._id ?? ''),
              clicks: typeof (a as any).clicks === 'number' ? (a as any).clicks : 0,
              redirects: typeof (a as any).redirects === 'number' ? (a as any).redirects : 0,
            }));
            setAds(normalized as AdConfig[]);
            // Compute a local sum as a safety net when server returns 0
            try {
              localAdViewsSum = (normalized as any[]).reduce((s, a) => s + Number((a as any)?.analytics?.views || (a as any)?.views || 0), 0);
            } catch {}
          }
        }
      } catch {}
      try {
        const data = await serviceGetSystemStats();
        if (data && typeof data === 'object') {
          // Merge server data onto mock defaults to avoid undefined/zeroed stats
          const merged: any = { ...mockSystemStats, ...data };
          // If server reports 0 ad views but local ads indicate activity, prefer the local sum
          if (Number(merged.totalAdViews || 0) === 0 && Number(localAdViewsSum || 0) > 0) {
            merged.totalAdViews = Number(localAdViewsSum);
          }
          // Overlay windowed global summary for range-accurate totals when available
          try {
            const range = (localStorage.getItem('admin_stats_range') || '7days');
            const summary = await serviceGetGlobalSummary(range);
            const totals = (summary && (summary as any).totals) || null;
            if (totals && typeof totals === 'object') {
              if (totals.views != null) merged.totalViews = Number(totals.views) || 0;
              if (typeof totals.uniqueVisitors === 'number') merged.uniqueVisitors = Number(totals.uniqueVisitors) || 0;
            }
          } catch {}
          setSystemStats(merged);
          try { localStorage.setItem('systemStats', JSON.stringify(merged)); } catch {}
        }
      } catch {}
    })();
  }, []);

  // Subscribe to server events for live admin updates; refetch system stats on bumps
  useEffect(() => {
    let closed = false;
    let retryTimer: any;
    let pollTimer: any;
    // Adaptive backoff for /api/system-stats 503 responses to avoid spamming server during DB unavailability.
    let backoffMs = 15000; // start with 15s
    let consecutive503 = 0;
    let esRef: any = null;
    const [initial] = [] as any;
    const setLiveMode = (mode: 'sse'|'poll'|'offline') => {
      try { (window as any).__adminLiveMode = mode; } catch {}
      try { if (setAdminLiveMode) setAdminLiveMode(mode); } catch {}
    };

    const refresh = async () => {
      // Do not refresh while checkout is active to avoid PaymentElement remount/flicker
  // Skip refresh gating by checkout state here; AdminDashboard runs independently of checkout flow
      try {
        // Direct fetch to inspect status codes (503 backoff handling) before applying service wrapper logic
        let data: any = null;
        let status: number | null = null;
        try {
          const resp = await fetch('/api/system-stats');
          status = resp.status;
          if (status === 503) {
            consecutive503 += 1;
            // Escalate backoff (double each time up to 5 minutes)
            backoffMs = Math.min(backoffMs * 2, 5 * 60 * 1000);
            // Clear existing interval-based polling
            try { if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } } catch {}
            // Close SSE stream if present
            try { if (esRef && esRef.close) esRef.close(); } catch {}
            setLiveMode('offline');
            // Schedule a one-shot retry using setTimeout
            try { pollTimer = setTimeout(() => { refresh().catch(() => {}); }, backoffMs); } catch {}
            return; // abort normal processing until service recovers
          }
          if (resp.ok) {
            try { data = await resp.json(); } catch { data = null; }
          }
        } catch {}
        // Reset backoff after first successful non-503 response
        if (status !== 503 && consecutive503 > 0) {
          consecutive503 = 0; backoffMs = 15000;
          // Re-establish regular polling interval (15s) if we are offline
          if (pollTimer && typeof pollTimer !== 'number') { /* no-op safeguard */ }
          if (!pollTimer) {
            try { pollTimer = setInterval(() => { refresh().catch(() => {}); }, 15000); setLiveMode(esRef ? 'sse' : 'poll'); } catch {}
          }
        }
        // If data was null (non-OK non-503), fall back to service wrapper (soft) for any additional parsing
        if (!data) {
          try { const svc = await serviceGetSystemStats(); if (svc && typeof svc === 'object') data = svc; } catch {}
        }
        if (data && typeof data === 'object') {
          const allowMock = Boolean((data as any)?.analyticsFlags?.enableMockStats);
          const merged = allowMock ? ({ ...mockSystemStats, ...data } as any) : ({ ...data } as any);
          // Overlay windowed global summary for more accurate totals
          try {
            const range = (localStorage.getItem('admin_stats_range') || '7days');
            const summary = await serviceGetGlobalSummary(range);
            const totals = (summary && (summary as any).totals) || null;
            if (totals && typeof totals === 'object') {
              if (totals.views != null) merged.totalViews = Number(totals.views) || 0;
              if (typeof totals.uniqueVisitors === 'number') merged.uniqueVisitors = Number(totals.uniqueVisitors) || 0;
            }
          } catch {}
          // Optional: fetch ads for admins and update ads state; also backfill totalAdViews when zero
          try {
            const token = localStorage.getItem('dev_admin_token');
            if (token) {
              const adsRes = await fetch('/api/admin/ads', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
              if (adsRes.ok) {
                const adsData = await adsRes.json();
                if (Array.isArray(adsData)) {
                  const normalized = (adsData as any[]).map((a:any) => ({
                    ...a,
                    id: String(a.id ?? a._id ?? ''),
                    clicks: typeof a.clicks === 'number' ? a.clicks : 0,
                    redirects: typeof a.redirects === 'number' ? a.redirects : 0,
                  }));
                  // Update ads list in admin views so per-ad stats reflect latest counts
                  setAds(normalized as any);
                  const sum = (normalized as any[]).reduce((s, a) => s + Number((a as any)?.analytics?.views || (a as any)?.views || 0), 0);
                  if (Number(merged.totalAdViews || 0) === 0 && sum > 0) merged.totalAdViews = sum;
                }
              }
            }
          } catch {}
          setSystemStats(merged);
          try { localStorage.setItem('systemStats', JSON.stringify(merged)); } catch {}
        }
      } catch {}
    };

    const connect = () => {
      try {
        const es = subscribeEvents({
          onAny: () => { refresh().catch(() => {}); },
          ready: () => { setLiveMode('sse'); },
          ping: () => { /* heartbeat */ },
          analytics_increment: () => {}, ad_view: () => {}, ad_click: () => {}, ad_redirect: () => {}
        });
        esRef = es;
        setLiveMode('sse');
        try { if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } } catch {}
        pollTimer = setInterval(() => { refresh().catch(() => {}); }, 15000);
        refresh().catch(() => {});
        return es as any;
      } catch {
        try { if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } } catch {}
        pollTimer = setInterval(() => { refresh().catch(() => {}); }, 15000);
        refresh().catch(() => {});
        setLiveMode('poll');
        return null as any;
      }
    };

    const es = connect();
    return () => {
      closed = true;
      try { if (es && (es as any).close) (es as any).close(); } catch {}
      try { if (retryTimer) clearTimeout(retryTimer); } catch {}
      try { if (pollTimer) clearInterval(pollTimer); } catch {}
    };
  }, []);

  // Expose admin live mode to child admin panels
  const [adminLiveMode, setAdminLiveMode] = React.useState<'sse'|'poll'|'offline'>(() => {
    try { return (window as any).__adminLiveMode || 'offline'; } catch { return 'offline'; }
  });

  // Cross-tab live refresh: listen for localStorage analytics signals and refetch server stats immediately
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      try {
        if (!e || e.key !== 'tcards_analytics_update') return;
        (async () => {
          try {
            const data = await serviceGetSystemStats();
            if (!data || typeof data !== 'object') return;
            const merged: any = { ...mockSystemStats, ...data };
            // Overlay windowed global summary
            try {
              const range = (localStorage.getItem('admin_stats_range') || '7days');
              const summary = await serviceGetGlobalSummary(range);
              const totals = (summary && (summary as any).totals) || null;
              if (totals && typeof totals === 'object') {
                if (totals.views != null) merged.totalViews = Number(totals.views) || 0;
                if (typeof totals.uniqueVisitors === 'number') merged.uniqueVisitors = Number(totals.uniqueVisitors) || 0;
              }
            } catch {}
            try {
              const token = localStorage.getItem('dev_admin_token');
              if (token) {
                const adsRes = await fetch('/api/admin/ads', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
                if (adsRes.ok) {
                  const adsData = await adsRes.json();
                  if (Array.isArray(adsData)) {
                    const normalized = (adsData as any[]).map((a:any) => ({
                      ...a,
                      id: String(a.id ?? a._id ?? ''),
                      clicks: typeof a.clicks === 'number' ? a.clicks : 0,
                      redirects: typeof a.redirects === 'number' ? a.redirects : 0,
                    }));
                    setAds(normalized as any);
                    const sum = (normalized as any[]).reduce((s, a) => s + Number((a as any)?.analytics?.views || (a as any)?.views || 0), 0);
                    if (Number(merged.totalAdViews || 0) === 0 && sum > 0) merged.totalAdViews = sum;
                  }
                }
              }
            } catch {}
            setSystemStats(merged);
            try { localStorage.setItem('systemStats', JSON.stringify(merged)); } catch {}
          } catch {}
        })();
      } catch {}
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // When an admin logs in after initial mount, load admin ads so SystemAdminPanel shows live stats
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('dev_admin_token');
        if (!token) return;
        const res = await fetch('/api/admin/ads', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data)) {
          const normalized = (data as any[]).map((a:any) => ({
            ...a,
            id: String(a.id ?? a._id ?? ''),
            clicks: typeof a.clicks === 'number' ? a.clicks : 0,
            redirects: typeof a.redirects === 'number' ? a.redirects : 0,
          }));
          setAds(normalized as any);
        }
      } catch {}
    })();
  }, [adminMode, adminActiveTab, adminSession]);

  // Load real users for system admin overview if admin token exists
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('dev_admin_token');
        if (!token) {
          // Fallback: if panel refreshed in child, it stores admin_users_cache; hydrate from it
          try {
            const raw = localStorage.getItem('admin_users_cache');
            if (raw) {
              const cache = JSON.parse(raw);
              if (Array.isArray(cache?.items) && cache.items.length) {
                const normalized = (cache.items as any[]).map((u:any) => ({ ...u, id: String(u.id || u._id || '') }));
                setUsers(normalized as any);
              }
            }
          } catch {}
          return; // no token: skip API call
        }
        const res = await fetch('/api/admin/users?limit=200', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({} as any));
        if (Array.isArray(data?.items)) {
          const normalized = (data.items as any[]).map((u) => ({
            ...u,
            id: String(u.id || u._id || ''),
          }));
          setUsers(normalized as any);
        }
      } catch {
        // ignore; fall back to initialUsers or cache
      }
    })();
  }, [adminMode, adminActiveTab]);

  // Normalize users once on mount to ensure each has a unique, stable id and username
  useEffect(() => {
    setUsers(prev => {
      const byKey = new Map<string, User>();
      const seenIds = new Set<string>();
      const norm = prev.map((u, idx) => {
        const emailKey = String((u as any).email || '').toLowerCase();
        let username = (u as any).username;
        if (!username || username === 'undefined') {
          const fromEmail = emailKey ? emailKey.split('@')[0] : '';
          const fromName = (u as any).name ? String((u as any).name).toLowerCase().replace(/\s+/g, '') : '';
          username = fromEmail || fromName || `user${idx}`;
        }
        let id = String((u as any).id || (u as any)._id || username);
        if (!id || id === 'undefined') id = `${username}_${idx}`;
        while (seenIds.has(id)) id = `${id}_${idx}`;
        seenIds.add(id);
        const merged = { ...u, id, username } as User;
        const key = emailKey || username || id;
        if (!byKey.has(key)) byKey.set(key, merged);
        return merged;
      });
      // Deduplicate by email/username key while preserving order of first occurrence
      const result: User[] = [];
      const emitted = new Set(byKey.keys());
      for (const u of norm) {
        const key = String((u as any).email || '').toLowerCase() || (u as any).username || (u as any).id;
        if (emitted.has(key)) {
          result.push(byKey.get(key) as User);
          emitted.delete(key);
        }
      }
      return result;
    });
  // run once
   
  }, []);

  // Clear dev admin session and return to non-admin view (no full reload)
  const handleAdminLogout = async () => {
    try {
      const token = localStorage.getItem('dev_admin_token');
      if (token) { try { await deleteAdminSession(token); } catch {} }
    } catch (e) {
      // ignore
    }
  // Note: do NOT clear owner_token / owner_session here — admin logout is
  // independent of the card-owner / CRM session.
  dispatchOwnerTokenEvent();
    try {
      localStorage.removeItem('dev_admin_token');
      localStorage.removeItem('dev_admin_role');
  // Also clear any initial hints so a future refresh won't resurrect admin view
  localStorage.removeItem('dev_admin_initial_tab');
  localStorage.removeItem('dev_admin_initial_mode');
      dispatchOwnerTokenEvent();
    } catch (e) {
      // ignore
    }
    setAdminSession(null);
    adminPreviewInitializedRef.current = false;
    setAdminPreviewUserId(null);
    setAdminSelfUser(null);
    setAdminPreviewExplicit(false);
    setAdminMode(authType === 'admin' ? 'system_admin' : 'card_owner');
    setAdminActiveTab('overview');
    safeToast('success', 'Logged out');
    if (onLogout) onLogout();
  };

  // --- Admin inactivity auto-logout (30 min) ---
  const adminInactivityRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ADMIN_INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes

  useEffect(() => {
    if (!adminSession) return;
    const role = localStorage.getItem('dev_admin_role');
    if (role !== 'superadmin' && role !== 'basicadmin') return;

    const resetTimer = () => {
      if (adminInactivityRef.current) clearTimeout(adminInactivityRef.current);
      adminInactivityRef.current = setTimeout(() => {
        handleAdminLogout();
        safeToast('info', 'Session expired due to inactivity. Please sign in again.');
      }, ADMIN_INACTIVITY_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer(); // start timer immediately

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (adminInactivityRef.current) clearTimeout(adminInactivityRef.current);
    };
  }, [adminSession]);

  const [currentUserIndex, setCurrentUserIndex] = useState(() => {
    if (!authenticatedUser) return 0;
    // Prefer to locate the authenticated user in the initial merged users list
    const idx = initialUsers.findIndex(u => u.id === authenticatedUser.id);
    return idx >= 0 ? idx : 0;
  });

  // If a dev user was just created and stored under localStorage.dev_users_last_created,
  // make that user the current index so the owner sees their card immediately.
  useEffect(() => {
    try {
      const last = localStorage.getItem('dev_users_last_created');
      if (!last) return;
      const idx = initialUsers.findIndex(u => u.id === last);
      if (idx !== -1) {
        setCurrentUserIndex(idx);
        // clear the marker after using it
        try { localStorage.removeItem('dev_users_last_created'); } catch (e) {}
      }
    } catch (e) {
      // ignore
    }
  }, [initialUsers]);

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  // Dedicated preview target for system admin (separate from bulk checkbox selection)
  const [adminPreviewUserId, setAdminPreviewUserId] = useState<string | null>(null);
  const [adminSelfUser, setAdminSelfUser] = useState<User | null>(null);
  const [adminPreviewExplicit, setAdminPreviewExplicit] = useState(false);
  const adminPreviewInitializedRef = useRef(false);

// 1) Load saved users once
useEffect(() => {
  try {
    const raw = localStorage.getItem('users');
    if (raw) setUsers(JSON.parse(raw));
  } catch (e) {
    console.warn('Failed to load users from storage', e);
  }
}, []);

// 2) Persist on every change
useEffect(() => {
  try {
    localStorage.setItem('users', JSON.stringify(users));
  } catch (e) {
    console.warn('Failed to persist users', e);
  }
}, [users]);

// (existing effects continue…)


  // sync homepage settings when parent changes
  useEffect(() => {
    if (parentHomepageSettings) {
      setHomepageSettings(mergeHomepageSettings(parentHomepageSettings));
    }
  }, [parentHomepageSettings]);

  // load subscription plans from localStorage once
  useEffect(() => {
    try {
      // Try server first
      (async () => {
        try {
          const token = localStorage.getItem('dev_admin_token');
          const serverPlans = await listSubscriptionPlans(token || undefined);
          if (Array.isArray(serverPlans) && serverPlans.length) {
            const parsed = serverPlans.map((p: any) => ({ id: p.id, name: p.name, price: p.price, description: p.description, features: p.features || [], buttonText: p.buttonText, buttonColor: p.buttonColor, isActive: p.isActive, isPopular: p.isPopular, createdAt: p.createdAt, updatedAt: p.updatedAt }));
            setSubscriptionPlans(parsed);
            return;
          }
        } catch (e) {
          // ignore and fall back to localStorage
        }

        const raw = localStorage.getItem('subscriptionPlans');
        if (!raw) return;
        const parsed: SubscriptionPlan[] = JSON.parse(raw);
        setSubscriptionPlans(parsed);
    // Do not inject local plans into homepage; server remains source of truth for public
      })();
    } catch (err) {
      console.error('Error loading subscription plans', err);
    }
  }, []);

  const safeGetUser = (index: number) => users[index] || users[0] || null;
  const currentUser = safeGetUser(currentUserIndex);
  const findUserInList = (id: string | null | undefined, list: User[] = users): User | null => {
    if (!id) return null;
    const sid = String(id);
    return list.find(u =>
      String(u.id) === sid ||
      String((u as any)._id) === sid ||
      String(u.username || '') === sid
    ) || null;
  };
  const resolveAdminPreviewUser = (): User | null => {
    // Default admin preview: always show the logged-in admin's own card from /api/me
    if (!adminPreviewExplicit) {
      if (adminSelfUser) return adminSelfUser;
      const loginEmail = String(adminSession?.email || '').toLowerCase();
      if (loginEmail) {
        const loginMatches = users.filter(u =>
          String((u as any).loginEmail || '').toLowerCase() === loginEmail
        );
        if (loginMatches.length === 1) return loginMatches[0];
        if (loginMatches.length > 1) {
          const scored = loginMatches.slice().sort((a, b) => {
            const s = (u: User) =>
              ((u as any).company ? 10 : 0) + ((u as any).position ? 10 : 0) +
              ((u as any).bio ? 5 : 0) + ((u as any).avatar ? 5 : 0);
            return s(b) - s(a);
          });
          return scored[0];
        }
        const byLoginEmail = users.find(u =>
          String((u as any).loginEmail || '').toLowerCase() === loginEmail
        );
        if (byLoginEmail) return byLoginEmail;
      }
      return null;
    }
    // Explicit preview choice from User Management
    const chosen = findUserInList(adminPreviewUserId);
    if (chosen) return chosen;
    return adminSelfUser || null;
  };
  // Display user resolution rules:
  // - system_admin: show adminPreviewUserId (defaults to logged-in admin's card)
  // - card_owner:
  //    • If admin has chosen "View as Card Owner", prefer the impersonated or selected user
  //    • Otherwise (real card owner session), use authenticatedUser
  // - view mode ('none'): fallback to currentUser
  const displayUser = adminMode === 'system_admin'
    ? resolveAdminPreviewUser()
    : (adminMode === 'card_owner')
      ? (() => {
          const preferredId = impersonatedUserId || selectedUsers[0] || null;
          if (preferredId) return findUserInList(preferredId) || currentUser;
          return authenticatedUser || currentUser;
        })()
      : currentUser;

  useEffect(() => {
    if (adminMode !== 'card_owner') {
      setCrmHeaderAccess({ show: false, active: false });
      return;
    }
    const crmRole = (displayUser as any)?.crmRole;
    if (!crmRole) {
      setCrmHeaderAccess({ show: false, active: false });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const access = await getCrmAccessStatus();
        if (cancelled) return;
        setCrmHeaderAccess({ show: !!access?.showCrmUi, active: !!access?.crmActive });
      } catch {
        if (!cancelled) setCrmHeaderAccess({ show: crmRole === 'client', active: false });
      }
    })();
    return () => { cancelled = true; };
  }, [adminMode, displayUser?.id, (displayUser as any)?.crmRole]);

  // analytics simulated side effect when viewing a card in 'none' mode
  const displayUserId = displayUser?.id;
useEffect(() => {
  if (adminMode !== 'none' || !displayUserId) return;
  handleAnalyticsUpdate(displayUserId, 'views');
  // Best-effort server-side increment for view counter and auto-expiry checks
  // Increment views (non-blocking) when viewing a card in neutral mode
  serviceIncrementMetric(String(displayUserId), 'views').catch(() => {});
  // runs only when the visible user changes in view mode
}, [adminMode, displayUserId]);

  // Ensure System Admin sees their own card in the left live preview (no duplicates)
  useEffect(() => {
    if (adminMode !== 'system_admin') return;
    const token = localStorage.getItem('dev_admin_token');
    if (!token) return;
    setAdminPreviewExplicit(false);
    adminPreviewInitializedRef.current = false;
    let cancelled = false;
    const eq = (a?: any, b?: any) => (a != null && b != null && String(a) === String(b));
    const eqCI = (a?: any, b?: any) => (a && b && String(a).toLowerCase() === String(b).toLowerCase());
    (async () => {
      try {
        const me = await getAdminSessionUser();
        if (!me || cancelled) return;
        setAdminSelfUser(me as User);
        let meId = String((me as any).id || (me as any)._id || (me as any).username || '');
        const adminLoginEmail = String(adminSession?.email || (me as any).loginEmail || '').toLowerCase();
        setUsers((prev) => {
          // Prefer loginEmail match — admin login email may differ from card contact email
          const idx = prev.findIndex((u) => (
            (adminLoginEmail && eqCI((u as any).loginEmail, adminLoginEmail)) ||
            eq(u.id, (me as any).id) || eq((u as any)._id, (me as any).id) ||
            eq(u.id, (me as any)._id) || eq((u as any)._id, (me as any)._id) ||
            eqCI(u.username, (me as any).username) || eqCI(u.email, (me as any).email) ||
            eqCI((u as any).loginEmail, (me as any).loginEmail)
          ));
          let next = prev.slice();
          if (idx >= 0) {
            next[idx] = { ...next[idx], ...me, loginEmail: (me as any).loginEmail || (next[idx] as any).loginEmail || adminLoginEmail } as any;
            setCurrentUserIndex(idx);
          } else {
            next = [{ ...(me as any), loginEmail: (me as any).loginEmail || adminLoginEmail }, ...next];
            setCurrentUserIndex(0);
          }
          // Default live preview to the logged-in admin's own card until explicitly changed
          if (!adminPreviewInitializedRef.current && !adminPreviewExplicit) {
            const previewId = String((me as any).id || (me as any)._id || '');
            if (previewId) {
              setAdminPreviewUserId(previewId);
              adminPreviewInitializedRef.current = true;
            }
          }
          // de-duplicate by normalized key (loginEmail || email || username || id)
          const seen = new Set<string>();
          const dedup: any[] = [];
          const keyOfUser = (u: any) => String(
            (u?.loginEmail || '').toLowerCase() ||
            (u?.email || '').toLowerCase() ||
            (u?.username || '').toLowerCase() ||
            (u?.id || u?._id || '')
          );
          for (const u of next) {
            const k = keyOfUser(u);
            if (!seen.has(k)) { seen.add(k); dedup.push(u); }
          }
          try { localStorage.setItem('users', JSON.stringify(dedup)); } catch {}
          return dedup;
        });

        // Hydrate full profile for the auto-selected admin user so preview has all details
        try {
          meId = String((me as any).id || (me as any)._id || '');
          if (meId) {
            const full = await getUser(meId).catch(() => null);
            if (!cancelled && full) {
              setAdminSelfUser(full as User);
              setUsers(prev => {
                const idx = prev.findIndex(u =>
                  String(u.id) === String((full as any).id) ||
                  String((u as any)._id) === String((full as any)._id) ||
                  String((u as any).username || '') === String((full as any).username || '') ||
                  (adminLoginEmail && String((u as any).loginEmail || '').toLowerCase() === adminLoginEmail)
                );
                if (idx >= 0) {
                  const next = prev.slice();
                  next[idx] = { ...prev[idx], ...(full as any), loginEmail: (full as any).loginEmail || (prev[idx] as any).loginEmail || adminLoginEmail } as any;
                  try { localStorage.setItem('users', JSON.stringify(next)); } catch {}
                  return next;
                }
                return prev;
              });
            }
          }
        } catch { /* ignore hydration errors */ }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [adminMode, adminSession?.id, adminSession?.email]);

  // user helpers
  const getUserKey = (u?: any) => (u?.id || u?._id || u?.username || '').trim();

  const handleUpdateUser = async (currentUserOrId: any, state: any) => {
    // Build whitelisted local patch from provided state (state may be a partial payload)
    const pick = <T extends object>(obj: any, keys: (keyof any)[]) => {
      const out: any = {};
      keys.forEach((k) => {
        if (typeof obj?.[k as any] !== 'undefined') out[k as any] = obj[k as any];
      });
      return out as T;
    };
    const basePatch = pick<Partial<User>>(state || {}, [
      // profile & visual fields
      'name','position','company','avatar','coverImage','bio','location',
      // contact
      'email','phone',
      // theme/customization
  'displayName','brandColor','colors','website','socialLinks','streamingLinks','links','logo','templateId','templateMetaId',
  // subscription
  'subscription',
      // account flags
      'isActive'
    ] as any);
    const localPatch: Partial<User> = {
      ...basePatch,
      theme: state?.theme && {
        primary: state.theme.primary,
        secondary: state.theme.secondary,
        background: state.theme.background,
        text: state.theme.text,
      },
    };

    // determine idOrUsername: if caller passed a string, use it; otherwise derive from object
    const idOrUsername = typeof currentUserOrId === 'string'
      ? String(currentUserOrId).trim()
      : getUserKey(currentUserOrId);

    if (!idOrUsername) {
      console.error('[App] cannot update: no user id/username provided', { currentUserOrId });
      safeToast('error', 'Cannot save: user not loaded');
      return false;
    }

  // allow callers to suppress global toast (e.g., auto-saves)
  const silent = state && (state as any).__silent === true;
  // prevent leaking control flags into state/server
  const { __silent, ...cleanPatch } = (localPatch || {}) as any;

  // optimistic local update: find matching local user by id/_id/username
  const matchPred = (u: any) => (u.id === idOrUsername || (u as any)._id === idOrUsername || u.username === idOrUsername);
  setUsers(prev => prev.map(u => (matchPred(u) ? { ...u, ...cleanPatch } : u)));

    try {
  const res = await updateUser(idOrUsername, cleanPatch as any);
      console.info('[App] user updated', res.user || res);
      const saved = res && (res as any).user ? (res as any).user : res;
      // Skip state churn if no effective changes
      setUsers(prev => prev.map(u => {
        const savedAny: any = (saved as any) || {};
        const savedUsername: string | undefined = typeof savedAny.username === 'string' && savedAny.username.trim() !== '' ? savedAny.username : undefined;
        const savedId: string | undefined = typeof savedAny.id === 'string' && savedAny.id.trim() !== '' ? savedAny.id : undefined;
        const savedMongoId: string | undefined = typeof savedAny._id === 'string' && savedAny._id.trim() !== '' ? savedAny._id : undefined;
        const isTarget = (
          matchPred(u)
          || (savedId && u.id === savedId)
          || (savedMongoId && (u as any)._id === savedMongoId)
          || (savedUsername && u.username === savedUsername)
        );
        if (!isTarget) return u;
        const merged: any = { ...u, ...savedAny };
        // Do not clobber stable identifiers with undefined/empty
        if (!savedUsername && u.username) merged.username = u.username;
        if (!savedId && u.id) merged.id = u.id;
        if (!savedMongoId && (u as any)._id) merged._id = (u as any)._id;
        try {
          const a = JSON.stringify({
            id: merged.id || (merged as any)._id || merged.username,
            colors: merged.colors,
            theme: merged.theme,
            brandColor: merged.brandColor,
            templateId: (merged as any).templateId,
            // include dynamic template id so switching to Soft Blossom triggers an update
            templateMetaId: (merged as any).templateMetaId,
          });
          const b = JSON.stringify({
            id: u.id || (u as any)._id || u.username,
            colors: (u as any).colors,
            theme: (u as any).theme,
            brandColor: (u as any).brandColor,
            templateId: (u as any).templateId,
            templateMetaId: (u as any).templateMetaId,
          });
          if (a === b) return u; // no-op
        } catch {}
        return merged;
      }));
      // If this update pertains to the logged-in card owner, mirror into authenticatedUser as well
      try {
        const savedAny: any = (saved as any) || {};
        const auth = authenticatedUser as any;
        const savedUsername: string | undefined = typeof savedAny.username === 'string' && savedAny.username.trim() !== '' ? savedAny.username : undefined;
        const savedId: string | undefined = typeof savedAny.id === 'string' && savedAny.id.trim() !== '' ? savedAny.id : undefined;
        const savedMongoId: string | undefined = typeof savedAny._id === 'string' && savedAny._id.trim() !== '' ? savedAny._id : undefined;
        const isAuthTarget = !!auth && (
          auth.id === idOrUsername || auth.username === idOrUsername || (auth as any)._id === idOrUsername
          || (savedId && auth.id === savedId)
          || (savedMongoId && (auth as any)._id === savedMongoId)
          || (savedUsername && auth.username === savedUsername)
        );
        if (isAuthTarget) {
          const mergedAuth: any = { ...auth, ...savedAny };
          if (!savedUsername && auth.username) mergedAuth.username = auth.username;
          if (!savedId && auth.id) mergedAuth.id = auth.id;
          if (!savedMongoId && (auth as any)._id) mergedAuth._id = (auth as any)._id;
          // @ts-expect-error setAuthenticatedUser is defined in AuthFlow component scope; here we are inside helper closure.
          setAuthenticatedUser(mergedAuth);
        }
      } catch {}
  if (!silent) safeToast('success', 'User saved');
      // keep a local snapshot for resilience
      try {
        setUsers(prev => {
          const snapshot = JSON.stringify(prev);
          localStorage.setItem('users', snapshot);
          return prev;
        });
      } catch {}
      return true;
    } catch (e: any) {
      console.error('[App] failed to persist user update', {
        message: e?.message, status: e?.status, url: e?.url, data: e?.data, context: e?.context,
      });
      try {
        const status = Number(e?.status || e?.data?.status || 0);
        const msg = String(e?.data?.message || e?.message || '').toLowerCase();
        const code = String((e?.data && (e.data.code || (e.data as any).errorCode)) || '').toUpperCase();
        const isDupEmail = (
          status === 409
          || code === 'EMAIL_TAKEN'
          || code === 'DUPLICATE_EMAIL'
          || (msg.includes('email') && (msg.includes('exists') || msg.includes('already')))
        );
        if (isDupEmail) {
          safeToast('warning', 'User exists with email. Kindly change email.');
        } else {
          safeToast('error', 'Failed to save to server; changes kept locally');
        }
      } catch {
        safeToast('error', 'Failed to save to server; changes kept locally');
      }
      return false;
    }
  };

  const handleUpdateUsers = (userIds: string[], updates: Partial<User>) => {
    // Build a whitelisted payload to avoid sending full user objects
    const buildPayload = (u: Partial<User>) => {
      const p: any = {};
      if (u.displayName !== undefined) p.displayName = u.displayName;
      if (u.brandColor !== undefined) p.brandColor = u.brandColor;
  if (u.theme !== undefined) p.theme = {
        primary: (u.theme as any)?.primary,
        secondary: (u.theme as any)?.secondary,
        background: (u.theme as any)?.background,
        text: (u.theme as any)?.text,
      };
  if ((u as any).templateId !== undefined) (p as any).templateId = (u as any).templateId;
      if ((u as any).templateMetaId !== undefined) (p as any).templateMetaId = (u as any).templateMetaId;
      return p;
    };

    // Optimistic local update
    setUsers(prev => prev.map(u => userIds.includes(u.id) ? { ...u, ...updates } : u));

    // Persist in parallel using typed API helper so errors include status/data/url
    (async () => {
      const isMongoId = (s?: string) => !!s && /^[a-f\d]{24}$/i.test(s);
      const payload = buildPayload(updates);

  const results = await Promise.allSettled(userIds.map(async id => {
        const localUser = users.find(u => u.id === id);
        const persistId = isMongoId(id) ? id : (localUser?.username || id);
        try {
          const res = await updateUserByIdOrUsername(persistId, payload as any);
          const saved = res && (res as any).user ? (res as any).user : res;
          return { id, saved };
        } catch (err: any) {
          // Keep the error object (toJson attaches .status/.data/.url)
          return { id, error: err };
        }
      }));

      const succeeded: string[] = [];
      results.forEach((r: any) => {
        if (r.status === 'fulfilled') {
          const val = r.value;
          if (val && val.saved) {
            const id = val.id;
            const saved: any = val.saved || {};
            const sUsername: string | undefined = typeof saved.username === 'string' && saved.username.trim() !== '' ? saved.username : undefined;
            const sId: string | undefined = typeof saved.id === 'string' && saved.id.trim() !== '' ? saved.id : undefined;
            const sMongo: string | undefined = typeof saved._id === 'string' && saved._id.trim() !== '' ? saved._id : undefined;
            setUsers(prev => prev.map(u => {
              const match = (u.id === id) || (sId && u.id === sId) || (sMongo && (u as any)._id === sMongo) || (sUsername && u.username === sUsername);
              if (!match) return u;
              const merged: any = { ...u, ...saved };
              if (!sUsername && u.username) merged.username = u.username;
              if (!sId && u.id) merged.id = u.id;
              if (!sMongo && (u as any)._id) merged._id = (u as any)._id;
              return merged;
            }));
            succeeded.push(val.id);
          } else if (val && val.error) {
            console.error('[App] bulk update item failed', { id: val.id, message: val.error?.message, status: val.error?.status, data: val.error?.data, url: val.error?.url });
          }
        } else {
          // Promise rejected (should be rare because we catch per-item)
          console.error('[App] bulk update promise rejected', r.reason || r);
        }
      });

      if (succeeded.length === userIds.length) safeToast('success', 'Users updated');
      else if (succeeded.length > 0) safeToast('warning', `Updated ${succeeded.length}/${userIds.length} users`);
      else safeToast('error', 'Bulk update failed; changes kept locally');
    })();
  };

  const handleDeleteUsers = (userIds: string[]) => {
    // Optimistically remove from UI
    setUsers(prev => prev.filter(u => !userIds.includes(u.id)));
    // Clear selection
    setSelectedUsers([]);
    
    // Persist deletions to server
    (async () => {
      const isMongoId = (s?: string) => !!s && /^[a-f\d]{24}$/i.test(s);
      
      const results = await Promise.allSettled(userIds.map(async id => {
        const localUser = users.find(u => u.id === id);
        const persistId = isMongoId(id) ? id : (localUser?.username || id);
        try {
          await deleteUserByAdmin(persistId);
          return { id, success: true };
        } catch (err: any) {
          console.error('[App] Failed to delete user', { id, error: err?.message || err });
          return { id, success: false, error: err };
        }
      }));

      const succeeded: string[] = [];
      const failed: string[] = [];
      
      results.forEach((r: any) => {
        if (r.status === 'fulfilled' && r.value?.success) {
          succeeded.push(r.value.id);
        } else {
          const id = r.value?.id || 'unknown';
          failed.push(id);
          // Restore failed deletions to UI
          const originalUser = users.find(u => u.id === id);
          if (originalUser) {
            setUsers(prev => [...prev, originalUser]);
          }
        }
      });

      if (failed.length === 0) {
        safeToast('success', `Deleted ${succeeded.length} user${succeeded.length !== 1 ? 's' : ''}`);
      } else if (succeeded.length > 0) {
        safeToast('warning', `Deleted ${succeeded.length}/${userIds.length} users (${failed.length} failed)`);
      } else {
        safeToast('error', 'Failed to delete users');
      }
    })();
  };

  // ads handlers
  const handleUpdateAd = (adId: string, updates: Partial<AdConfig>) => {
    // optimistic local update
    setAds(prev => prev.map(a => a.id === adId ? { ...a, ...updates } : a));
    // persist to server (if available)
    (async () => {
      try {
        const token = localStorage.getItem('dev_admin_token');
        const res = await fetch(`/api/admin/ads/${encodeURIComponent(adId)}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(updates), credentials: 'include' });
        if (!res.ok) {
          console.warn('[App] Failed to PATCH ad on server', await res.text());
        } else {
          try {
            const data = await res.json();
            const sid = (data && (data.id ?? data._id)) ? String(data.id ?? data._id) : adId;
            setAds(prev => prev.map(a => (String(a.id) === sid ? {
              ...a,
              ...data,
              id: sid,
              clicks: typeof data?.clicks === 'number' ? data.clicks : (a.clicks || 0),
              redirects: typeof data?.redirects === 'number' ? data.redirects : (a.redirects || 0),
            } : a)));
          } catch {}
        }
      } catch (e) {
        console.warn('[App] Failed to PATCH ad', e);
      }
    })();
  };
  const handleAddAd = (newAd: Omit<AdConfig, 'id'>) => {
    // try to create on server first
    (async () => {
      try {
        const token = localStorage.getItem('dev_admin_token');
        const res = await fetch('/api/admin/ads', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(newAd), credentials: 'include' });
        if (res.ok) {
          const created = await res.json();
          const sid = (created && (created.id ?? created._id)) ? String(created.id ?? created._id) : String(Date.now());
          setAds(prev => [...prev, { ...created, id: sid, clicks: created.clicks || 0, redirects: created.redirects || 0 }]);
          return;
        }
        console.warn('[App] Server rejected create ad:', await res.text());
      } catch (e) {
        console.warn('[App] Failed to POST ad', e);
      }
      // fallback to local-only ad
      const ad: AdConfig = { ...newAd, id: Date.now().toString(), clicks: 0, redirects: 0 };
      setAds(prev => [...prev, ad]);
    })();
  };
  const handleDeleteAd = (adId: string) => {
    // optimistic local removal
    setAds(prev => prev.filter(a => a.id !== adId));
    (async () => {
      try {
        const token = localStorage.getItem('dev_admin_token');
        const res = await fetch(`/api/admin/ads/${encodeURIComponent(adId)}`, { method: 'DELETE', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }, credentials: 'include' });
        if (!res.ok) console.warn('[App] Failed to DELETE ad on server', await res.text());
      } catch (e) {
        console.warn('[App] Failed to DELETE ad', e);
      }
    })();
  };

  // payment/email/sms gateways
  const handleUpdatePaymentGateway = (gatewayId: string, updates: Partial<PaymentGateway>) => {
    setPaymentGateways(prev => prev.map(g => g.id === gatewayId ? { ...g, ...updates } : g));
  };
  const handleAddPaymentGateway = (newGateway: Omit<PaymentGateway, 'id'>) => {
    setPaymentGateways(prev => [...prev, { ...newGateway, id: Date.now().toString() }]);
  };
  const handleDeletePaymentGateway = async (gatewayId: string) => {
    setPaymentGateways(prev => prev.filter(g => g.id !== gatewayId));
    // Persist deletion to server by removing the provider key from paymentSettings
    try {
      const { paymentSettings: current } = await getPaymentSettings();
      const ps = { ...current };
      // Map gateway id to the server-side paymentSettings key
      const keyMap: Record<string, string> = { stripe: 'stripe', visa: 'stripe', google_pay: 'stripe', mpesa: 'mpesa', tigopesa: 'tigopesa', airtel_money: 'airtel', pesapal: 'pesapal' };
      const serverKey = keyMap[gatewayId];
      if (serverKey && ps[serverKey]) {
        delete ps[serverKey];
        await updatePaymentSettings(ps);
      }
    } catch (e) {
      console.warn('[App] failed to persist gateway deletion', (e as any)?.message);
    }
  };

  const handleUpdateEmailGateway = (gatewayId: string, updates: any) => {
    setEmailGateways(prev => prev.map(g => g.id === gatewayId ? { ...g, ...updates } : g));
  };
  const handleAddEmailGateway = (newGateway: any) => {
    setEmailGateways(prev => [...prev, { ...newGateway, id: Date.now().toString() }]);
  };
  const handleDeleteEmailGateway = (gatewayId: string) => {
    setEmailGateways(prev => prev.filter(g => g.id !== gatewayId));
  };

  const handleUpdateSMSGateway = (gatewayId: string, updates: any) => {
    setSmsGateways(prev => prev.map(g => g.id === gatewayId ? { ...g, ...updates } : g));
  };
  const handleAddSMSGateway = (newGateway: any) => {
    setSmsGateways(prev => [...prev, { ...newGateway, id: Date.now().toString() }]);
  };
  const handleDeleteSMSGateway = (gatewayId: string) => {
    setSmsGateways(prev => prev.filter(g => g.id !== gatewayId));
  };

  // SUBSCRIPTION PLANS: use functional updates to avoid stale closures and always persist the "updated" array
  const persistSubscriptionPlansAndHomepage = (plans: SubscriptionPlan[]) => {
    try {
      // Persist to server (if available), else fallback to localStorage
      (async () => {
        try {
          const token = localStorage.getItem('dev_admin_token');
          // If not authenticated, skip server writes to avoid 401/500 noise; we'll persist locally below
          if (token) {
            // upsert each plan via POST/PATCH as needed
            for (const p of plans) {
              if (!p.id || String(p.id).startsWith('tmp_')) {
                const created = await createSubscriptionPlan({ name: p.name, price: p.price, description: p.description, features: p.features || [], buttonText: p.buttonText, buttonColor: p.buttonColor, isActive: p.isActive, isPopular: p.isPopular }, token);
                if (created && created.id) p.id = created.id;
              } else {
                await updateSubscriptionPlan(String(p.id), { name: p.name, price: p.price, description: p.description, features: p.features || [], buttonText: p.buttonText, buttonColor: p.buttonColor, isActive: p.isActive, isPopular: p.isPopular }, token);
              }
            }
            // finally refresh server copy into local state
            try {
              const serverPlans = await listSubscriptionPlans(token);
              const mapped = (serverPlans || []).map((p: any) => ({ id: p.id, name: p.name, price: p.price, description: p.description, features: p.features || [], buttonText: p.buttonText, buttonColor: p.buttonColor, isActive: p.isActive, isPopular: p.isPopular, createdAt: p.createdAt, updatedAt: p.updatedAt }));
              setSubscriptionPlans(mapped);
              if (Array.isArray(mapped)) {
                try { localStorage.setItem('subscriptionPlans', JSON.stringify(mapped)); } catch (e) {}
                // Update dynamic server fallback cache so public homepage fallback matches latest server plans
                // This keeps BasicAdminPanelV2 and other views synchronized without duplicating fallback logic.
                try {
                  const fallbackShape = mapped.map(p => ({ name: p.name, price: p.price, description: p.description, features: p.features, buttonText: p.buttonText, buttonColor: p.buttonColor, isPopular: p.isPopular }));
                  localStorage.setItem('server_plans_cache', JSON.stringify(fallbackShape));
                  localStorage.setItem('server_plans_cache_ts', Date.now().toString());
                } catch {}
              }
            } catch (e) {
              // ignore
            }
          }
        } catch (err) {
          // ignore server errors and fall through to localStorage fallback
        }

        // fallback: persist locally
        try { localStorage.setItem('subscriptionPlans', JSON.stringify(plans)); } catch (e) { console.warn('Could not persist subscriptionPlans locally:', e); }
        // Also refresh server plans cache fallback (even if unauthenticated, we treat local edited plans as provisional fallback)
        // Central cache write ensures homepage + BasicAdminPanelV2 continue to display latest edited plans if server fetch fails.
        try {
          const fallbackShape = (plans || []).map(p => ({ name: p.name, price: p.price, description: p.description, features: p.features || [], buttonText: p.buttonText || 'Get Started', buttonColor: p.buttonColor || 'bg-blue-600 hover:bg-blue-700', isPopular: !!p.isPopular }));
          localStorage.setItem('server_plans_cache', JSON.stringify(fallbackShape));
          localStorage.setItem('server_plans_cache_ts', Date.now().toString());
        } catch {}
        // Also mirror into homepageSettings for instant left-hand preview responsiveness
        try {
          setHomepageSettings((prev: any) => {
            const prevPricing = (prev && (prev as any).pricing) ? (prev as any).pricing : {};
            const nextPlans = (plans || [])
              .filter(p => p && (p.isActive ?? true))
              .map(p => ({
                name: p.name,
                price: p.price,
                description: p.description || '',
                features: Array.isArray(p.features) ? p.features : [],
                buttonText: p.buttonText || 'Get Started',
                buttonColor: p.buttonColor || 'bg-blue-600 hover:bg-blue-700',
                isPopular: !!p.isPopular,
              }));
            return {
              ...(prev || {}),
              pricing: { ...prevPricing, plans: nextPlans },
            };
          });
        } catch {}
      })();
    } catch (err) {
      console.error('Error saving subscription plans:', err);
    }
  };

  const handleUpdateSubscriptionPlan = (planId: string, updates: Partial<SubscriptionPlan>) => {
    setSubscriptionPlans(prev => {
      const updated = prev.map(p => p.id === planId ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p);
      persistSubscriptionPlansAndHomepage(updated);
      return updated;
    });
  };

  const handleAddSubscriptionPlan = (newPlan: Omit<SubscriptionPlan, 'id' | 'createdAt' | 'updatedAt'>) => {
    setSubscriptionPlans(prev => {
      // Remove any prior unsaved tmp_ plan to avoid multiple add panels
      const withoutTmp = prev.filter(p => !(typeof p.id === 'string' && p.id.startsWith('tmp_')));
      const plan: SubscriptionPlan = {
        ...newPlan,
        id: `tmp_${Date.now().toString()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as SubscriptionPlan;
      const updated = [...withoutTmp, plan];
      persistSubscriptionPlansAndHomepage(updated);
      return updated;
    });
  };

  const handleDeleteSubscriptionPlan = (planId: string) => {
    // Optimistically remove from local state and homepage pricing
    setSubscriptionPlans(prev => {
      const updated = prev.filter(p => p.id !== planId);
      persistSubscriptionPlansAndHomepage(updated);
      return updated;
    });

    // Attempt server-side deletion if authenticated and the id looks persisted (not a temporary id)
    (async () => {
      const token = localStorage.getItem('dev_admin_token');
      const isTemp = !planId || planId.startsWith('tmp_');
      if (!token) {
        // Not logged in: local preview only
        try { safeToast('info', 'Deleted locally. Login as admin to delete on server.'); } catch {}
        return;
      }
      if (isTemp) return; // nothing to delete on server yet
      try {
        const res = await fetch(`/api/admin/subscription-plans/${encodeURIComponent(String(planId))}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include'
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => '');
          console.warn('[App] Failed to DELETE subscription plan on server', res.status, txt);
          try { safeToast('error', 'Server deletion failed'); } catch {}
        } else {
          try { safeToast('success', 'Subscription plan deleted'); } catch {}
          try {
            // Refresh admin list of subscription plans
            const serverPlans = await listSubscriptionPlans(token);
            const mapped = (serverPlans || []).map((p: any) => ({ id: p.id, name: p.name, price: p.price, description: p.description, features: p.features || [], buttonText: p.buttonText, buttonColor: p.buttonColor, isActive: p.isActive, isPopular: p.isPopular, createdAt: p.createdAt, updatedAt: p.updatedAt }));
            setSubscriptionPlans(mapped);
            try { localStorage.setItem('subscriptionPlans', JSON.stringify(mapped)); } catch {}
          } catch {}

          // Trigger a public settings refresh so homepage picks up server-side merged plans
          try {
            const site = await getSiteSettings();
            const p = site?.homepageSettings || {};
            setHomepageSettings((prev: any) => ({
              ...prev,
              ...p,
              pricing: (() => {
                const serverPricing = (p as any)?.pricing || {};
                const serverPlans = Array.isArray(serverPricing?.plans) && serverPricing.plans.length > 0 ? serverPricing.plans : null;
                if (serverPlans) return { ...serverPricing, plans: serverPlans };
                const prevPlans = (prev as any)?.pricing?.plans;
                if (Array.isArray(prevPlans) && prevPlans.length > 0) return { ...serverPricing, plans: prevPlans };
                try {
                  const raw = localStorage.getItem('server_plans_cache');
                  if (raw) {
                    const arr = JSON.parse(raw);
                    if (Array.isArray(arr) && arr.length > 0) return { ...serverPricing, plans: arr };
                  }
                } catch {}
                return { ...serverPricing, plans: Array.isArray(defaultHomepageSettings.pricing?.plans) ? defaultHomepageSettings.pricing.plans : [] };
              })(),
            }));
          } catch {}
        }
      } catch (e) {
        console.warn('[App] Error deleting subscription plan', e);
      }
    })();
  };

  // homepage settings handler
  const handleUpdateHomepageSettings = (updates: any) => {
    setHomepageSettings((prev: any) => {
      const merged = mergeHomepageSettings({ ...prev, ...updates }) as any;
      if (updates && typeof updates === 'object' && 'pricing' in updates) {
        const incomingPricing: any = updates.pricing || {};
        const incomingPlans = incomingPricing.plans;
        const prevPlans = prev?.pricing?.plans;
        if ((incomingPlans === undefined || (Array.isArray(incomingPlans) && incomingPlans.length === 0)) && Array.isArray(prevPlans) && prevPlans.length > 0) {
          merged.pricing = { ...merged.pricing, ...incomingPricing, plans: prevPlans };
        }
        if (Array.isArray(incomingPlans) && incomingPlans.length > 0) {
          try { localStorage.setItem('server_plans_cache', JSON.stringify(incomingPlans)); } catch {}
        }
      }
      return merged;
    });
    parentUpdateHomepageSettings(updates);

  // 2) If pricing.plans changed in Homepage Customization, mirror into subscriptionPlans
    const incomingPlans = (updates && updates.pricing && Array.isArray(updates.pricing.plans) && updates.pricing.plans.length > 0)
      ? updates.pricing.plans as Array<{ name: string; price: string; description?: string; features?: string[]; buttonText?: string; buttonColor?: string; isPopular?: boolean }>
      : null;

    if (incomingPlans) {
      setSubscriptionPlans(prev => {
        const byName = new Map<string, SubscriptionPlan>();
        for (const p of prev) {
          const key = String(p.name || '').trim().toLowerCase();
          if (key) byName.set(key, p);
        }

        const now = new Date().toISOString();
        const next: SubscriptionPlan[] = incomingPlans.map((ip, i) => {
          const key = String(ip.name || '').trim().toLowerCase();
          const existing = key ? byName.get(key) : undefined;
          const base: SubscriptionPlan = existing ? existing : {
            id: `tmp_${Date.now()}_${i}`,
            name: ip.name,
            price: ip.price,
            description: ip.description || '',
            features: Array.isArray(ip.features) ? ip.features : [],
            buttonText: ip.buttonText || 'Get Started',
            buttonColor: ip.buttonColor || 'bg-blue-600 hover:bg-blue-700',
            isActive: true,
            isPopular: !!ip.isPopular,
            createdAt: now,
            updatedAt: now
          } as SubscriptionPlan;
          return {
            ...base,
            name: ip.name,
            price: ip.price,
            description: ip.description || '',
            features: Array.isArray(ip.features) ? ip.features : [],
            buttonText: ip.buttonText || base.buttonText,
            buttonColor: ip.buttonColor || base.buttonColor,
            isPopular: !!ip.isPopular,
            isActive: true,
            updatedAt: now
          } as SubscriptionPlan;
        });

        // Any existing plans not present by name are set inactive (preserve for server-side IDs)
        const namesIn = new Set(incomingPlans.map(p => String(p.name || '').trim().toLowerCase()));
        for (const old of prev) {
          const key = String(old.name || '').trim().toLowerCase();
          if (!namesIn.has(key)) {
            next.push({ ...old, isActive: false, updatedAt: now });
          }
        }

  // Persist to server/local, but do not force homepage pricing; server endpoint will reflect changes
  persistSubscriptionPlansAndHomepage(next);
        return next;
      });
    }
  };

  const handleSaveHomepageSettings = () => {
    (async () => {
      const token = localStorage.getItem('dev_admin_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const payload = { homepageSettings: mergeHomepageSettings(homepageSettings) };
      try {
        const resp = await fetch('/api/admin/site-settings', { method: 'PUT', headers, body: JSON.stringify(payload), credentials: 'include' });
        if (resp.ok) {
          setHomepageSettings(payload.homepageSettings);
          try {
            localStorage.setItem('homepageSettings', JSON.stringify(payload.homepageSettings));
            localStorage.setItem('homepageSettings_update_ts', String(Date.now()));
          } catch {}
          try { parentUpdateHomepageSettings(payload.homepageSettings); } catch {}
          try { parentSaveHomepageSettings(); } catch {}
          safeToast('success', 'Homepage settings saved');
          // Refresh server cache consumers
          try {
            const data = await getSiteSettings();
            if (data?.homepageSettings) {
              localStorage.setItem('homepage_server_cache', JSON.stringify({ at: Date.now(), homepageSettings: data.homepageSettings }));
              if (data.homepageSettings?.pricing?.plans) localStorage.setItem('server_plans_cache', JSON.stringify(data.homepageSettings.pricing.plans));
            }
          } catch {}
          return;
        }
        if (resp.status === 403) {
          safeToast('error', 'You don’t have permission to save. Ask a superadmin to grant access.');
          return;
        }
        // Non-OK (network or server error)
        safeToast('error', 'Failed to save to server. Please try again.');
      } catch {
        safeToast('error', 'Network error while saving.');
      }
    })();
  };

  const handleUpdateCaptchaSettings = (updates: Partial<CaptchaSettings>) => {
    setCaptchaSettings((prev: CaptchaSettings) => ({ ...prev, ...updates }));
  };

  const handleSaveCaptchaSettings = () => {
    try {
      localStorage.setItem('captchaSettings', JSON.stringify(captchaSettings));
      console.log('CAPTCHA settings saved');
    } catch (err) {
      console.error('Error saving CAPTCHA settings', err);
    }
  };

  const handleUpdateEmailOTPSettings = (updates: Partial<EmailOTPSettings>) => {
    setEmailOTPSettings((prev: EmailOTPSettings) => ({ ...prev, ...updates }));
  };

  const handleSaveEmailOTPSettings = () => {
    (async () => {
      const token = localStorage.getItem('dev_admin_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      
      // Include emailOTP settings in homepageSettings
      const payload = { 
        homepageSettings: {
          ...homepageSettings,
          emailOTP: emailOTPSettings
        }
      };
      
      try {
        const resp = await fetch('/api/admin/site-settings', { 
          method: 'PUT', 
          headers, 
          body: JSON.stringify(payload), 
          credentials: 'include' 
        });
        
        if (resp.ok) {
          // Update local state
          setHomepageSettings((prev: HomepageSettings) => ({
            ...prev,
            emailOTP: emailOTPSettings
          }));
          
          // Save to localStorage as backup
          localStorage.setItem('emailOTPSettings', JSON.stringify(emailOTPSettings));
          
          safeToast('success', 'Email OTP settings saved successfully');
          
          // Refresh server cache
          try {
            const data = await getSiteSettings();
            if (data?.homepageSettings) {
              localStorage.setItem('homepage_server_cache', JSON.stringify({ 
                at: Date.now(), 
                homepageSettings: data.homepageSettings 
              }));
              // Update emailOTP settings from server response
              if (data.homepageSettings.emailOTP) {
                setEmailOTPSettings(data.homepageSettings.emailOTP);
              }
            }
          } catch {}
          return;
        }
        
        if (resp.status === 403) {
          safeToast('error', 'You do not have permission to save. Ask a superadmin to grant access.');
          return;
        }
        
        // Non-OK (network or server error)
        safeToast('error', 'Failed to save to server. Please try again.');
      } catch {
        safeToast('error', 'Network error while saving.');
      }
    })();
  };
  // lead handlers (now server-backed with graceful fallback)
  const handleLeadSubmit = (leadData: Omit<Lead, 'id' | 'submittedAt'>) => {
    (async () => {
      try {
        const ownerId = (leadData as any).cardOwnerId;
        const ownerEmail = (leadData as any).cardOwnerEmail || users.find(u => (u as any).id === ownerId || (u as any)._id === ownerId)?.email || '';
        const payload = {
          cardOwnerId: ownerId,
          cardOwnerEmail: ownerEmail,
          cardOwnerName: (leadData as any).cardOwnerName || users.find(u => (u as any).id === ownerId || (u as any)._id === ownerId)?.name || '',
          fullName: (leadData as any).fullName,
          email: (leadData as any).email,
          phoneNumber: (leadData as any).phoneNumber,
          message: (leadData as any).message,
          source: (leadData as any).source || 'email_form',
        } as any;
        const created = await apiCreateLead(payload);
        setLeads(prev => [created as any, ...prev]);
      } catch (e) {
        // fallback to local append if server fails
        const existingUser = users.find(u =>
          (u.email || '').toLowerCase() === (leadData as any).email?.toLowerCase()
          || (u.phone || '') === (leadData as any).phoneNumber
        );
  const newLead: any = {
          ...leadData,
          id: `lead_${Date.now()}`,
          submittedAt: new Date().toISOString(),
          isExistingUser: !!existingUser,
          existingUserId: (existingUser as any)?.id || (existingUser as any)?._id,
        };
        setLeads(prev => [newLead, ...prev]);
      }
    })();
  };

  const handleMarkLeadAsRead = (leadId: string) => {
    (async () => {
      try {
        const updated = await apiMarkLeadAsRead(leadId);
        setLeads(prev => prev.map(l => l.id === leadId ? (updated as any) : l));
      } catch {
        setLeads(prev => prev.map(l => l.id === leadId ? { ...l, isRead: !l.isRead } : l));
      }
    })();
  };

  const handleDeleteLead = (leadId: string) => {
    (async () => {
      try { await apiDeleteLead(leadId); } catch {}
      setLeads(prev => prev.filter(l => l.id !== leadId));
    })();
  };

  const handleAddLeadTag = (leadId: string, tag: string) => {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, tags: [...(l.tags || []), tag] } : l));
  };

  // Load leads for current mode/user (scope to displayed user in card_owner mode)
  useEffect(() => {
    (async () => {
      try {
        setLeadsUnauthorized(false);
        if (adminMode === 'system_admin') {
          const list = await listAllLeads().catch((err: any) => {
            if (err && (err.status === 401 || err.status === 403)) setLeadsUnauthorized(true);
            return [];
          });
          if (Array.isArray(list) && list.length) setLeads(list as any);
          else setLeads((mockLeads as any));
        } else if (adminMode === 'card_owner') {
          const ownerId = (displayUser as any)?.id || (displayUser as any)?._id || '';
          if (ownerId) {
            // Require an owner token before attempting to fetch leads and avoid console 401s
            let hasToken = false;
            try { hasToken = !!(localStorage.getItem('dev_admin_token') || localStorage.getItem('owner_token')); } catch {}
            if (!hasToken) {
              setLeadsUnauthorized(true);
              setLeads([]);
              return;
            }
            const list = await listLeadsByOwner(String(ownerId)).catch((err: any) => {
              if (err && (err.status === 401 || err.status === 403)) setLeadsUnauthorized(true);
              return [];
            });
            if (Array.isArray(list)) setLeads(list as any);
            else setLeads([]);
          } else {
            setLeads([]);
          }
        }
      } catch {}
    })();
  }, [adminMode, authenticatedUser, displayUser?.id, ownerAuthVersion]);

  const hydrateUserForPreview = (userId: string) => {
    (async () => {
      try {
        const fresh = await getUser(userId);
        if (fresh && (fresh as any).id) {
          setUsers(prev => {
            const idx = prev.findIndex(u =>
              String(u.id) === String((fresh as any).id) ||
              String((u as any)._id) === String((fresh as any)._id) ||
              String((u as any)._id) === String((fresh as any).id)
            );
            if (idx >= 0) {
              const next = prev.slice();
              next[idx] = { ...prev[idx], ...(fresh as any) } as any;
              try { localStorage.setItem('users', JSON.stringify(next)); } catch {}
              return next;
            }
            return prev;
          });
        }
      } catch {}
    })();
  };

  const handleUserSelect = (userId: string) => {
    // Bulk checkbox selection only — does not change the left-hand live preview
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleAdminPreviewUserChange = (userId: string) => {
    setAdminPreviewUserId(userId);
    setAdminPreviewExplicit(true);
    adminPreviewInitializedRef.current = true;
    const userIndex = users.findIndex(u =>
      String(u.id) === String(userId) ||
      String((u as any)._id) === String(userId)
    );
    if (userIndex !== -1) setCurrentUserIndex(userIndex);
    hydrateUserForPreview(userId);
  };

  // Debounced system stats refresh for system admin after local analytics increments
  const scheduleSystemStatsRefresh = React.useMemo(() => {
    // Use dynamic ES module import (avoids CommonJS require which breaks in ESM/browser bundles)
    let trigger: (() => void) | null = null;
    // Immediately queue a microtask to load the module; returned function is a no-op until ready
    (async () => {
      try {
        const mod = await import('./utils/debouncedRefresher');
        const createDebouncedRefresher = (mod as any).createDebouncedRefresher as typeof import('./utils/debouncedRefresher').createDebouncedRefresher;
        trigger = createDebouncedRefresher({
          minIntervalMs: 750,
            shouldRun: () => adminMode === 'system_admin',
            refresh: async () => {
              try {
                const data = await serviceGetSystemStats();
                if (data && typeof data === 'object') {
                  const merged: any = { ...mockSystemStats, ...data };
                  setSystemStats(merged);
                  try { localStorage.setItem('systemStats', JSON.stringify(merged)); } catch {}
                }
              } catch {}
            }
        });
      } catch (e) {
        // Swallow to avoid crashing render path; could optionally log
        // console.warn('Failed to init debounced refresher', e);
      }
    })();
    return () => { if (trigger) { trigger(); } };
  }, [adminMode]);

  const handleAnalyticsUpdate = (userId: string, metric: keyof User['analytics'] | 'email_form') => {
    // Map any front-end only pseudo metrics to canonical server metrics
    const canonical: Record<string, keyof User['analytics']> = {
      email_form: 'messageClicks'
    } as any;
    const mapped: keyof User['analytics'] = (canonical as any)[metric] || metric as keyof User['analytics'];

    // Optimistic local update so UI feels responsive
    setUsers(prev => prev.map(u => {
      if (u.id === userId) {
        return { ...u, analytics: { ...u.analytics, [mapped]: (u.analytics?.[mapped] || 0) + 1 } };
      }
      return u;
    }));

    // Persist via service layer (auto-captures UTM/src); handle quota & hydrate
    (async () => {
      try {
        const result: any = await serviceIncrementMetric(String(userId), String(mapped));
        if (!result?.ok) {
          const errCode = result?.error || result?.reason;
          if (errCode === 'share_limit_free' && mapped === 'shares') {
            const msg = result?.message || 'In order to share more, kindly upgrade your package';
            try { safeToast('info', msg); } catch {}
          }
          // Revert optimistic increment on failure or quota rejection
          setUsers(prev => prev.map(u => u.id === userId
            ? { ...u, analytics: { ...u.analytics, [mapped]: Math.max(0, (u.analytics?.[mapped] || 1) - 1) } }
            : u));
        } else {
          const serverTotals = result?.analytics;
          if (serverTotals && typeof serverTotals === 'object') {
            setUsers(prev => prev.map(u => u.id === userId
              ? { ...u, analytics: { ...u.analytics, ...serverTotals } }
              : u));
          }
        }
      } catch (e) {
        console.warn('analytics increment network error', (e as any)?.message || e);
        setUsers(prev => prev.map(u => u.id === userId
          ? { ...u, analytics: { ...u.analytics, [mapped]: Math.max(0, (u.analytics?.[mapped] || 1) - 1) } }
          : u));
      }
      // Debounced refresh after attempt
      scheduleSystemStatsRefresh();
    })();
    // Also trigger refresh early (optimistic) for admin to reduce perceived latency
    scheduleSystemStatsRefresh();
  };

  // State to hold temporary preview changes from card owner edits
  const [previewUser, setPreviewUser] = React.useState<Partial<User> | null>(null);

  // Merge helper: overlay only meaningful values from preview (avoid blank/empty clobber)
  function overlayNonEmpty<T extends Record<string, any>>(base: T, overlay?: Partial<T> | null): T {
    if (!overlay) return base;
    const out: any = { ...base };
    for (const [k, v] of Object.entries(overlay)) {
      if (v === undefined || v === null) continue;
      if (typeof v === 'string' && v.trim() === '') continue;
      if (Array.isArray(v) && v.length === 0) continue;
      if (typeof v === 'object' && !Array.isArray(v)) {
        if (Object.keys(v as any).length === 0) continue;
      }
      (out as any)[k] = v;
    }
    return out as T;
  }

  // Merge live display user with transient preview overrides without wiping with blanks
  const mergedDisplayUser = displayUser ? overlayNonEmpty(displayUser as any, previewUser as any) as User : null;
  const safeDisplayUser = React.useMemo(() => {
    if (!mergedDisplayUser) return null;
    const u: any = { ...mergedDisplayUser };
    // Ensure strings are defined to avoid conditional blanks in templates
    u.name = typeof u.name === 'string' ? u.name : '';
    u.position = typeof u.position === 'string' ? u.position : '';
    u.company = typeof u.company === 'string' ? u.company : '';
    u.website = typeof u.website === 'string' ? u.website : '';
    u.bio = typeof u.bio === 'string' ? u.bio : '';
    // Normalize links/objects to objects
    u.socialLinks = (u.socialLinks && typeof u.socialLinks === 'object') ? u.socialLinks : (u.social && typeof u.social === 'object' ? u.social : {});
    u.streamingLinks = (u.streamingLinks && typeof u.streamingLinks === 'object') ? u.streamingLinks : {};
    // Normalize template identifiers to strings so preview logic is consistent after refresh
    if (typeof u.templateId === 'number') {
      u.templateId = String(u.templateId);
    } else if (typeof u.templateId !== 'string') {
      // keep undefined if not provided; do not force default here
      if (u.templateId != null) u.templateId = String(u.templateId);
    } else {
      u.templateId = u.templateId.trim();
    }
    if (typeof u.templateMetaId === 'number') {
      u.templateMetaId = String(u.templateMetaId);
    } else if (typeof u.templateMetaId === 'string') {
      u.templateMetaId = u.templateMetaId.trim();
      // Normalize sentinel string values to absence so classic templates render correctly
      if (u.templateMetaId === 'null' || u.templateMetaId === 'undefined' || u.templateMetaId === '') {
        delete (u as any).templateMetaId;
      }
    }

    // Normalize classic template identifiers (support legacy slug/builtin forms)
    const classicSlugMap: Record<string,string> = {
      'professional-classic': '1',
      'elegant-looking': '2',
      'modern-clean': '3',
      'golden-view': '4',
      'classic-modular': '5'
    };
    if (typeof u.templateId === 'string') {
      const raw = u.templateId.trim().toLowerCase();
      // builtin-n → n
      const m = raw.match(/^builtin-(\d)$/);
      if (m) u.templateId = m[1];
      else if (classicSlugMap[raw]) u.templateId = classicSlugMap[raw];
    }

    // Explicit preview precedence fix:
    // - If owner is actively previewing a dynamic template (previewUser.templateMetaId), drop any classic id
    // - If owner is actively previewing a classic template (previewUser.templateId in 1..5), drop any dynamic meta
    const p: any = previewUser || null;
  const previewTplId = typeof p?.templateId === 'string' ? p.templateId.trim() : (typeof (p?.templateId as any) === 'number' ? String(p?.templateId) : '');
  const previewMetaId = typeof p?.templateMetaId === 'string' ? p.templateMetaId.trim() : (typeof (p?.templateMetaId as any) === 'number' ? String(p?.templateMetaId) : '');
    const fallbackClassicId = (() => {
      const raw = (u as any)?.templateClassicFallbackId;
      const val = typeof raw === 'number' ? String(raw) : (typeof raw === 'string' ? raw.trim() : '');
      return ['1','2','3','4','5'].includes(val) ? val : '';
    })();
    // Enforce the immediate signup selection if present in localStorage and matching this user
    try {
      if (!fallbackClassicId) {
        const selRaw = localStorage.getItem('template_selection_user');
        if (selRaw) {
          const sel = JSON.parse(selRaw);
          const selEmail = String((sel || {}).email || '').trim().toLowerCase();
          const uEmail = String((u || {}).email || '').trim().toLowerCase();
          const selTpl = String((sel || {}).templateId || '').trim();
          if (selEmail && uEmail && selEmail === uEmail && ['1','2','3','4','5'].includes(selTpl)) {
            (u as any).templateId = selTpl;
          }
          const selClassic = String((sel || {}).templateClassicFallbackId || '').trim();
          if (!['1','2','3','4','5'].includes((u as any).templateId || '') && ['1','2','3','4','5'].includes(selClassic)) {
            (u as any).templateId = selClassic;
          }
        }
      }
    } catch {}
    if (!previewMetaId && fallbackClassicId) {
      const hasClassic = typeof u.templateId === 'string' && ['1','2','3','4','5'].includes(u.templateId);
      const hasMeta = typeof (u as any).templateMetaId === 'string' && ((u as any).templateMetaId || '').trim().length > 0;
      if (!hasClassic && !hasMeta) u.templateId = fallbackClassicId;
    }
    if (previewMetaId) {
      delete u.templateId;
      u.templateMetaId = previewMetaId; // ensure meta is visible to the preview renderer
    } else if (previewTplId && ['1','2','3','4','5'].includes(previewTplId)) {
      delete u.templateMetaId; // critical: clear stale meta so classic renders
      u.templateId = previewTplId;
    } else {
      // Preserve dynamic templates by only defaulting when no meta/template is present
      const hasMetaTemplate = typeof (u as any).templateMetaId === 'string' && ((u as any).templateMetaId || '').trim().length > 0;
  const hasClassicTemplate = typeof u.templateId === 'string' && ['1','2','3','4','5'].includes(u.templateId);
      if (!hasClassicTemplate && !hasMetaTemplate) {
        // In card owner view, avoid defaulting to classic to prevent incorrect fallback flashes
        // when a dynamic template selection is persisting/hydrating.
        if (adminMode !== 'card_owner') {
          u.templateId = '1';
        } else if (fallbackClassicId) {
          u.templateId = fallbackClassicId;
        }
      }
      // If both classic and meta are present, keep both:
      // - renderTemplate will use classic immediately when meta isn't hydrated yet
      // - once meta is available, dynamic will take over automatically
      if (hasMetaTemplate && hasClassicTemplate) {
        // keep both; no deletion here
      } else if (hasMetaTemplate) {
        // Only meta present: ensure we don't accidentally render a mismatched classic fallback
        delete (u as any).templateId;
      }
    }

    return u as User;
  }, [mergedDisplayUser, adminMode, previewUser]);

  const _safeDisplayTemplateId = safeDisplayUser?.templateId;
  const _safeDisplayTemplateMetaId = (safeDisplayUser as any)?.templateMetaId as string | undefined;
  React.useEffect(() => {
    const metaId = _safeDisplayTemplateMetaId;
    if (!metaId) return;
    const cacheKey = 'templates_cache_meta';

    const readCachedMeta = () => {
      if ((templatesMetaById as any)?.[metaId]) return (templatesMetaById as any)[metaId];
      try {
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(cacheKey) : null;
        if (raw) {
          const parsed = JSON.parse(raw || '{}');
          if (parsed && typeof parsed === 'object' && parsed[metaId]) return parsed[metaId];
        }
      } catch {}
      try {
        const fromWin = (window as any).__TEMPLATES_META_BY_ID__;
        if (fromWin && fromWin[metaId]) return fromWin[metaId];
      } catch {}
      return null;
    };

    const cached = readCachedMeta();
    if (cached) {
      mergeTemplateMetaCache({ [metaId]: cached });
      return;
    }

    let cancelled = false;

    const hydrateMeta = async () => {
      let listProvidedMeta = false;
      try {
        const list: any[] = await getTemplatesMeta(true) as any[];
        if (cancelled) return;
        const map: Record<string, any> = {};
        (list || []).forEach((it: any) => { if (it && it.id) map[it.id] = it; });
        if (Object.keys(map).length > 0) {
          mergeTemplateMetaCache(map);
          listProvidedMeta = !!map[metaId];
        }
      } catch {
        listProvidedMeta = false;
      }

      if (cancelled || listProvidedMeta) return;

      const retryCache = readCachedMeta();
      if (retryCache) {
        mergeTemplateMetaCache({ [metaId]: retryCache });
        return;
      }

      try {
        const single = await getTemplateMetaById(metaId);
        if (cancelled || !single || !(single as any).id) return;
        mergeTemplateMetaCache({ [(single as any).id]: single });
      } catch {}
    };

    hydrateMeta();
    return () => { cancelled = true; };
  }, [_safeDisplayTemplateMetaId, templatesMetaById, mergeTemplateMetaCache]);

  // Ensure owner left live preview can hydrate dynamic meta even when only previewUser carries the metaId.
  // This prevents the preview from stalling on "Generating live preview…" when the meta is not yet cached.
  React.useEffect(() => {
    const previewMetaId = (previewUser as any)?.templateMetaId as string | undefined;
    if (!previewMetaId) return;
    // If meta already present, nothing to do
    let meta: any = (templatesMetaById as any)?.[previewMetaId] || null;
    if (!meta) {
      try {
        const raw = localStorage.getItem('templates_cache_meta');
        if (raw) {
          const parsed = JSON.parse(raw || '{}');
          if (parsed && typeof parsed === 'object') meta = (parsed as any)[previewMetaId] || null;
        }
      } catch {}
    }
    if (!meta) {
      const fromWin: any = (window as any).__TEMPLATES_META_BY_ID__;
      if (fromWin && previewMetaId in fromWin) meta = fromWin[previewMetaId];
    }
    if (meta) {
      mergeTemplateMetaCache({ [previewMetaId]: meta });
      return;
    }

    // Fetch single meta and cache it for immediate use by MetaPreviewCard
    (async () => {
      try {
        const m = await getTemplateMetaById(previewMetaId);
        if (m && (m as any).id) {
          mergeTemplateMetaCache({ [(m as any).id]: m });
        }
      } catch {}
    })();
  }, [previewUser, templatesMetaById, mergeTemplateMetaCache]);

  // Preview-first decision: honor live edits from CardOwnerPanel before falling back to stored user
  // Merge preview data (like bio, name, etc.) into the display user for live preview
  const effectiveDisplayUser = React.useMemo(() => {
    if (!previewUser || !safeDisplayUser) return safeDisplayUser;
    return {
      ...safeDisplayUser,
      ...previewUser,
      // Ensure critical fields from safeDisplayUser aren't lost
      id: safeDisplayUser.id,
      email: safeDisplayUser.email,
      username: safeDisplayUser.username,
    };
  }, [safeDisplayUser, previewUser]);

  const renderTemplate = () => {
    if (!safeDisplayUser) return <div className="text-center p-6">No user selected</div>;

    const templateProps = {
      user: safeDisplayUser,
      ads,
      // Templates expect metric as string; convert keyof to string here
      onAnalyticsUpdate: (userId: string, metric: string) => handleAnalyticsUpdate(userId, metric as any),
      isOwnerView: adminMode === 'card_owner' && safeDisplayUser?.id === currentUser?.id,
      onLeadSubmit: handleLeadSubmit,
      leads
    };

    // Shared classic slug->id map for all fallback branches
    const classicSlugMapGlobal: Record<string, string> = {
      'professional-classic': '1',
      'elegant-looking': '2',
      'modern-clean': '3',
      'golden-view': '4',
      'classic-modular': '5'
    };
    const mapClassicKeyToId = (key?: string) => {
      const k = String(key || '').trim().toLowerCase();
      return classicSlugMapGlobal[k] || '';
    };
    const debugPreview = (() => {
      try { return localStorage.getItem('debug_preview_selection') === '1'; } catch { return false; }
    })();

    // If neither a dynamic nor a classic template is specified (owner view during hydration),
    // show a neutral placeholder instead of defaulting to classic.
    const classicFallbackForUser = (() => {
      const raw = (safeDisplayUser as any)?.templateClassicFallbackId;
      const val = typeof raw === 'number' ? String(raw) : (typeof raw === 'string' ? raw.trim() : '');
      return ['1','2','3','4','5'].includes(val) ? val : '';
    })();
    const hasClassicOrFallback = !!safeDisplayUser.templateId || !!classicFallbackForUser;

    if (!(safeDisplayUser as any)?.templateMetaId && !hasClassicOrFallback && adminMode === 'card_owner') {
      return (
        <div className="w-full flex items-center justify-center py-10">
          <div className="rounded-xl border bg-white shadow-sm px-4 py-3 text-sm text-gray-600">Loading your template…</div>
        </div>
      );
    }

    let previewClassicId = '';
    try {
      const preview = (previewUser as any) || null;
      const previewTplId = String(preview?.templateId || '').trim();
      const previewTplKey = String(preview?.templateKey || '').trim().toLowerCase();
      // Prefer explicit numeric id; otherwise map preview key to classic id
      previewClassicId = (['1','2','3','4','5'].includes(previewTplId) ? previewTplId : mapClassicKeyToId(previewTplKey));
      const previewMetaId = (preview?.templateMetaId || '') as string;

      // 1) If user is actively previewing a dynamic template (meta present), render it
      if (previewMetaId) {
        let meta: any = (templatesMetaById as any)?.[previewMetaId] || null;
        if (!meta) {
          try {
            const raw = localStorage.getItem('templates_cache_meta');
            if (raw) {
              const parsed = JSON.parse(raw || '{}');
              if (parsed && typeof parsed === 'object') meta = (parsed as any)[previewMetaId] || null;
            }
          } catch {}
        }
        if (!meta) {
          const fromWin: any = (window as any).__TEMPLATES_META_BY_ID__;
          if (fromWin && previewMetaId in fromWin) meta = fromWin[previewMetaId];
        }
        // Classic preview only when explicitly selected via previewUser or stored user templateId
        const isClassicPreview = (
          ['1','2','3','4','5'].includes(previewTplId)
          || ['1','2','3','4','5'].includes(String(safeDisplayUser.templateId||''))
        );
        if (meta && (meta.renderer === 'dynamic-v1' || (meta as any).spec)) {
          // Owner actions remain gated by adminMode, but media fallback should be consistent:
          // Always use system DEFAULT_COVER when user has no cover, even in System Admin view
          const isOwnerView = adminMode === 'card_owner';
          const cover = (effectiveDisplayUser as any).coverImage
            || (effectiveDisplayUser as any).coverUrl
            || DEFAULT_COVER;
          return (
      <MetaPreviewCard
              template={meta}
              user={{
                ...(effectiveDisplayUser as any),
        // normalize avatar/cover for dynamic renderer
        avatarUrl: (effectiveDisplayUser  as any).avatarUrl || (effectiveDisplayUser as any).avatar,
        coverUrl: (effectiveDisplayUser as any).coverUrl || cover,
        coverImage: (effectiveDisplayUser as any).coverImage || cover,
              }}
              accent={(effectiveDisplayUser as any)?.colors?.accent}
              mode="full"
              frame="none"
              allowInnerScroll={true}
              ads={ads}
              isOwnerView={isOwnerView}
            />
          );
        } else if (!isClassicPreview) {
          // Meta not yet hydrated. Render an immediate classic fallback using current template key
          const cover = (effectiveDisplayUser as any).coverImage
            || (effectiveDisplayUser as any).coverUrl
            || DEFAULT_COVER;
          // Prefer explicit classic selections over heuristic templateKey mapping
          const variantKeyFallback = String((preview as any)?.templateKey || (effectiveDisplayUser as any)?.card?.templateKey || (effectiveDisplayUser as any)?.templateKey || '').toLowerCase();
          const classicSlugMap: Record<string,string> = {
            'professional-classic': '1',
            'elegant-looking': '2',
            'modern-clean': '3',
            'golden-view': '4',
            'classic-modular': '5'
          };
          const ensureClassic = (v?: string) => (v && ['1','2','3','4','5'].includes(v) ? v : '');
          // Resolve a single effective classic id first, then only use heuristic templateKey mapping if absent
          const effectiveClassicId = (() => {
            // 0) If preview provided a templateKey, use it immediately
            const fromPreviewKey = mapClassicKeyToId(previewTplKey);
            if (fromPreviewKey) return fromPreviewKey;
            const fromPreview = ensureClassic(previewTplId);
            if (fromPreview) return fromPreview;
            const fromUser = ensureClassic((effectiveDisplayUser as any).templateId);
            if (fromUser) return fromUser;
            const fromFallback = ensureClassic(classicFallbackForUser);
            if (fromFallback) return fromFallback;
            try {
              const selRaw = localStorage.getItem('template_selection_user');
              if (selRaw) {
                const sel = JSON.parse(selRaw);
                const selEmail = String((sel || {}).email || '').trim().toLowerCase();
                const uEmail = String((effectiveDisplayUser as any)?.email || '').trim().toLowerCase();
                const selTpl = ensureClassic(String((sel || {}).templateId || (sel || {}).templateClassicFallbackId || ''));
                if (selEmail && uEmail && selEmail === uEmail && selTpl) return selTpl;
              }
            } catch {}
            return '';
          })();
          const tplId = effectiveClassicId || classicSlugMap[variantKeyFallback] || '1';
          if (debugPreview) {
            try {
              console.debug('[preview:decision] meta-not-hydrated', {
                branch: 'preview.meta.fallback-classic',
                previewTplId,
                previewTplKey,
                mappedFromPreviewKey: mapClassicKeyToId(previewTplKey),
                userTemplateId: (effectiveDisplayUser as any).templateId,
                userTemplateKey: (effectiveDisplayUser as any)?.templateKey,
                userCardTemplateKey: (effectiveDisplayUser as any)?.card?.templateKey,
                classicFallbackForUser,
                variantKeyFallback,
                tplId,
              });
            } catch {}
          }
          const classicProps = {
            user: {
              ...(effectiveDisplayUser as any),
              avatarUrl: (effectiveDisplayUser as any).avatarUrl || (effectiveDisplayUser as any).avatar,
              coverUrl: (effectiveDisplayUser as any).coverUrl || cover,
              coverImage: (effectiveDisplayUser as any).coverImage || cover,
            },
            ads,
            onAnalyticsUpdate: (userId: string, metric: string) => handleAnalyticsUpdate(userId, metric as any),
            isOwnerView: adminMode === 'card_owner' && effectiveDisplayUser?.id === currentUser?.id,
            onLeadSubmit: handleLeadSubmit,
            leads
          };
          switch (tplId) {
            case '2':
              return <ECardTemplate2 {...classicProps} />;
            case '3':
              return <ECardTemplate3 {...classicProps} />;
            case '4':
              return <ECardTemplate4 {...classicProps} />;
            case '5':
              return <ECardTemplate5 {...classicProps} />;
            default:
              return <ECardTemplate1 {...classicProps} />;
          }
        } else {
          // Classic template chosen but meta stale/missing: fall through to classic switch by skipping this branch
        }
      }

      // 2) If actively previewing a classic selection, force classic rendering (ignore any stored meta)
      if (previewTplId && ['1','2','3','4','5'].includes(previewTplId)) {
        // fall through to classic switch below
      } else {
        // 3) No active preview override: prefer dynamic when a templateMetaId exists
        const metaId = (effectiveDisplayUser as any).templateMetaId as string | undefined;
        if (metaId) {
          let meta: any = (templatesMetaById as any)?.[metaId] || null;
          if (!meta) {
            try {
              const raw = localStorage.getItem('templates_cache_meta');
              if (raw) {
                const parsed = JSON.parse(raw || '{}');
                if (parsed && typeof parsed === 'object') meta = (parsed as any)[metaId] || null;
              }
            } catch {}
          }
          if (!meta) {
            const fromWin: any = (window as any).__TEMPLATES_META_BY_ID__;
            if (fromWin && metaId in fromWin) meta = fromWin[metaId];
          }
          if (meta && (meta.renderer === 'dynamic-v1' || (meta as any).spec)) {
            const isOwnerView = adminMode === 'card_owner';
            const cover = (effectiveDisplayUser as any).coverImage
              || (effectiveDisplayUser as any).coverUrl
              || DEFAULT_COVER;
            return (
              <MetaPreviewCard
                template={meta}
                user={{
                  ...(effectiveDisplayUser as any),
                  avatarUrl: (effectiveDisplayUser as any).avatarUrl || (effectiveDisplayUser as any).avatar,
                  coverUrl: (effectiveDisplayUser as any).coverUrl || cover,
                  coverImage: (effectiveDisplayUser as any).coverImage || cover,
                }}
                accent={(effectiveDisplayUser as any)?.colors?.accent}
                mode="full"
                frame="none"
                allowInnerScroll={true}
                ads={ads}
                isOwnerView={isOwnerView}
              />
            );
          }
          // Meta not yet available: render immediate classic fallback based on current template key
          const cover = (effectiveDisplayUser as any).coverImage
            || (effectiveDisplayUser as any).coverUrl
            || DEFAULT_COVER;
          // Prefer explicit classic selections over heuristic templateKey mapping
          const variantKeyFallback = String((preview as any)?.templateKey || (effectiveDisplayUser as any)?.card?.templateKey || (effectiveDisplayUser as any)?.templateKey || '').toLowerCase();
          const classicSlugMap: Record<string,string> = {
            'professional-classic': '1',
            'elegant-looking': '2',
            'modern-clean': '3',
            'golden-view': '4',
            'classic-modular': '5'
          };
          const ensureClassic = (v?: string) => (v && ['1','2','3','4','5'].includes(v) ? v : '');
          // Resolve a single effective classic id first, then only use heuristic templateKey mapping if absent
          const effectiveClassicId = (() => {
            // 0) If preview provided a templateKey, use it immediately
            const fromPreviewKey = mapClassicKeyToId((previewUser as any)?.templateKey);
            if (fromPreviewKey) return fromPreviewKey;
            const fromPreview = ensureClassic(previewClassicId);
            if (fromPreview) return fromPreview;
            const fromUser = ensureClassic((effectiveDisplayUser as any).templateId);
            if (fromUser) return fromUser;
            const fromFallback = ensureClassic(classicFallbackForUser);
            if (fromFallback) return fromFallback;
            try {
              const selRaw = localStorage.getItem('template_selection_user');
              if (selRaw) {
                const sel = JSON.parse(selRaw);
                const selEmail = String((sel || {}).email || '').trim().toLowerCase();
                const uEmail = String((effectiveDisplayUser as any)?.email || '').trim().toLowerCase();
                const selTpl = ensureClassic(String((sel || {}).templateId || (sel || {}).templateClassicFallbackId || ''));
                if (selEmail && uEmail && selEmail === uEmail && selTpl) return selTpl;
              }
            } catch {}
            return '';
          })();
          const tplId = effectiveClassicId || classicSlugMap[variantKeyFallback] || '1';
          if (debugPreview) {
            try {
              console.debug('[preview:decision] user-meta-not-hydrated', {
                branch: 'user.meta.fallback-classic',
                previewClassicId,
                previewTemplateKey: (previewUser as any)?.templateKey,
                mappedFromPreviewKey: mapClassicKeyToId((previewUser as any)?.templateKey),
                userTemplateId: (effectiveDisplayUser as any).templateId,
                userTemplateKey: (effectiveDisplayUser as any)?.templateKey,
                userCardTemplateKey: (effectiveDisplayUser as any)?.card?.templateKey,
                classicFallbackForUser,
                variantKeyFallback,
                tplId,
              });
            } catch {}
          }
          const classicProps = {
            user: {
              ...(effectiveDisplayUser as any),
              avatarUrl: (effectiveDisplayUser as any).avatarUrl || (effectiveDisplayUser as any).avatar,
              coverUrl: (effectiveDisplayUser as any).coverUrl || cover,
              coverImage: (effectiveDisplayUser as any).coverImage || cover,
            },
            ads,
            onAnalyticsUpdate: (userId: string, metric: string) => handleAnalyticsUpdate(userId, metric as any),
            isOwnerView: adminMode === 'card_owner' && effectiveDisplayUser?.id === currentUser?.id,
            onLeadSubmit: handleLeadSubmit,
            leads
          };
          switch (tplId) {
            case '2':
              return <ECardTemplate2 {...classicProps} />;
            case '3':
              return <ECardTemplate3 {...classicProps} />;
            case '4':
              return <ECardTemplate4 {...classicProps} />;
            case '5':
              return <ECardTemplate5 {...classicProps} />;
            default:
              return <ECardTemplate1 {...classicProps} />;
          }
        }
      }
    } catch {}

  const resolvedClassicTemplateId = (() => {
    if (previewClassicId && ['1','2','3','4','5'].includes(previewClassicId)) return previewClassicId;
    const tpl = effectiveDisplayUser.templateId;
    if (tpl && ['1','2','3','4','5'].includes(tpl)) return tpl;
    if (classicFallbackForUser) return classicFallbackForUser;
    return tpl;
  })();

  if (debugPreview) {
    try {
      console.debug('[preview:decision] final', {
        branch: 'final.classic.switch',
        previewClassicId,
        userTemplateId: (effectiveDisplayUser as any)?.templateId,
        classicFallbackForUser,
        resolvedClassicTemplateId,
      });
    } catch {}
  }

  switch (resolvedClassicTemplateId) {
      case '2':
        return <ECardTemplate2 {...templateProps} />;
      case '3':
        return <ECardTemplate3 {...templateProps} />;
      case '4':
        return <ECardTemplate4 {...templateProps} />;
      case '5':
        return <ECardTemplate5 {...templateProps} />;
      // If template '4' exists import and return it here. Otherwise fallback:
      default:
        return <ECardTemplate1 {...templateProps} />;
    }
  };

  // Trial enforcement side-effect
  useEffect(() => {
  setUsers(prev => {
    let changed = false;
    const next = prev.map(user => {
      if (user.subscription?.type === 'trial' && (user.analytics?.views || 0) >= 50 && user.isActive) {
        changed = true;
        return { ...user, isActive: false };
      }
      return user;
    });
    return changed ? next : prev; // prevent infinite loop when nothing changes
  });
}, [users]);

  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-6 lg:py-8">
  <div className={`${authType === 'admin' ? 'max-w-7xl' : 'max-w-6xl'} mx-auto px-4 sm:px-6 min-w-0`}>
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-0 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Tcards Digital Business Cards</h1>
            <p className="text-sm sm:text-base text-gray-600">
              {authType === 'admin'
                ? 'Professional e-card templates with comprehensive admin controls'
                : `Welcome back, ${authenticatedUser?.name || 'Card Owner'}! Manage your digital business card`
              }
            </p>
          </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
            {authType === 'admin' && (
              <select
                value={adminMode}
                onChange={(e) => {
                  const next = e.target.value as typeof adminMode;
                  if (next !== 'card_owner') setImpersonatedUserId(null);
                  setAdminMode(next);
                }}
                className="px-3 py-2 border rounded-lg text-xs sm:text-sm self-start sm:self-center"
              >
                <option value="system_admin">{(effectiveAdminRole === 'basicadmin' || adminActiveTab === 'basic') ? 'Basic Admin' : 'System Admin'}</option>
                <option value="none">View Mode</option>
                <option value="card_owner">Card Owner</option>
              </select>
            )}

            {(adminMode === 'card_owner' || authType !== 'admin') ? (
              <div className="flex flex-wrap items-center justify-end gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setOwnerRequestedTab('overview')}
                  className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium text-xs sm:text-sm cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap ${
                    adminMode === 'card_owner' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  <UserIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">Card Owner</span>
                  <span className="sm:hidden">Owner</span>
                </button>

                {crmHeaderAccess.show && (
                  <button
                    onClick={() => setOwnerRequestedTab('crm')}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-sm hover:from-indigo-700 hover:to-purple-700 transition-all text-xs sm:text-sm font-medium whitespace-nowrap"
                    title={crmHeaderAccess.active ? t('crmHeaderOpenManagement') : t('crmHeaderCompleteSubscription')}
                  >
                    <Briefcase className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('crmHeaderManagement')}</span>
                    <span className="sm:hidden">{t('crmHeaderManagementShort')}</span>
                  </button>
                )}

                <OwnerMiniWebsiteButton />

                <button
                  onClick={handleAdminLogout}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-[#6b0f1a] to-[#8b1c2b] text-white rounded-lg shadow-sm hover:from-[#7a1620] hover:to-[#9a2633] text-xs sm:text-sm font-medium whitespace-nowrap"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setOwnerRequestedTab('overview')}
                  className={`flex items-center justify-center px-3 py-2 rounded-lg font-medium text-xs sm:text-sm cursor-pointer hover:opacity-90 transition-opacity ${
                    adminMode === 'system_admin' ? (effectiveAdminRole === 'basicadmin' ? 'bg-amber-500 text-white' : 'bg-red-600 text-white') :
                      'bg-gray-200 text-gray-700'
                  }`}
                >
                  {adminMode === 'system_admin' ? (effectiveAdminRole === 'basicadmin' ? <Shield className="w-4 h-4 mr-2" /> : <Crown className="w-4 h-4 mr-2" />) :
                    <Eye className="w-4 h-4 mr-2" />}
                  <span className="hidden sm:inline">
                    {adminMode === 'system_admin' ? (effectiveAdminRole === 'basicadmin' ? 'Basic Admin' : 'System Admin') : 'View Mode'}
                  </span>
                </button>
              </>
            )}

            {/* show current admin session (if any) */}
            {adminSession && (
              <div className="flex items-center gap-3">
                {/* Notification bell with dropdown */}
                <div ref={bellRef} className="relative">
                  <button
                    onClick={() => setBellDropdownOpen(o => !o)}
                    className="relative p-2 rounded-full hover:bg-gray-200 transition-colors"
                    title={badgeCount > 0 ? `${badgeCount} notification${badgeCount > 1 ? 's' : ''}` : 'Notifications'}
                  >
                    <Bell className="w-5 h-5 text-gray-600" />
                    {badgeCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold leading-none text-white bg-red-600 rounded-full px-1"
                        style={{ animation: headerUnattendedWd > 0 ? 'notif-bell-pulse 1.5s ease-in-out infinite' : undefined }}
                      >
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                    <style>{`
                      @keyframes notif-bell-pulse {
                        0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(220,38,38,0.4); }
                        50% { transform: scale(1.2); box-shadow: 0 0 8px 2px rgba(220,38,38,0.5); }
                      }
                    `}</style>
                  </button>

                  {/* Notification dropdown panel */}
                  {bellDropdownOpen && (
                    <div className="absolute right-0 sm:right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px] bg-white rounded-xl shadow-2xl border border-gray-200/80 z-[9999] overflow-hidden"
                      style={{ animation: 'notif-dropdown-in 0.15s ease-out' }}>
                      <style>{`
                        @keyframes notif-dropdown-in {
                          from { opacity: 0; transform: translateY(-8px) scale(0.96); }
                          to { opacity: 1; transform: translateY(0) scale(1); }
                        }
                      `}</style>
                      {/* Header */}
                      <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-gray-100 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-gray-900 text-sm">Notifications</h3>
                            <p className="text-xs text-gray-500 mt-0.5">{visibleNotifItems.length > 0 ? `${visibleNotifItems.reduce((s,i)=>s+i.count,0)} items need attention` : 'All caught up'}</p>
                          </div>
                          {visibleNotifItems.length > 0 && (
                            <div className="flex items-center gap-1">
                              <button onClick={markAllRead} className="p-1.5 rounded-lg hover:bg-white/80 transition-colors" title="Mark all read">
                                <Check className="w-3.5 h-3.5 text-gray-500" />
                              </button>
                              <button onClick={dismissAllNotifs} className="p-1.5 rounded-lg hover:bg-white/80 transition-colors" title="Clear all">
                                <Trash2 className="w-3.5 h-3.5 text-gray-500" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Items */}
                      <div className="max-h-[360px] overflow-y-auto">
                        {visibleNotifItems.length === 0 ? (
                          <div className="py-10 px-4 text-center">
                            <Bell className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                            <p className="text-sm text-gray-500 font-medium">No pending notifications</p>
                            <p className="text-xs text-gray-400 mt-1">You&apos;re all caught up!</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {visibleNotifItems.map(item => {
                              const isRead = readNotifs.has(item.key);
                              const severityColors = {
                                critical: { bg: 'bg-red-50', border: 'border-l-red-500', badge: 'bg-red-100 text-red-700' },
                                warning: { bg: 'bg-amber-50', border: 'border-l-amber-500', badge: 'bg-amber-100 text-amber-700' },
                                info: { bg: 'bg-blue-50', border: 'border-l-blue-500', badge: 'bg-blue-100 text-blue-700' },
                              }[item.severity];
                              return (
                                <div key={item.key}
                                  className={`px-4 py-3 border-l-[3px] ${severityColors.border} ${isRead ? 'bg-gray-50/50' : severityColors.bg} hover:bg-gray-50 transition-colors group`}
                                >
                                  <div className="flex items-start gap-3">
                                    <span className="text-lg mt-0.5 shrink-0">{item.icon}</span>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className={`text-sm font-semibold ${isRead ? 'text-gray-500' : 'text-gray-900'}`}>{item.title}</span>
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${severityColors.badge}`}>{item.count}</span>
                                      </div>
                                      <p className={`text-xs mt-0.5 ${isRead ? 'text-gray-400' : 'text-gray-600'}`}>{item.desc}</p>
                                      <div className="flex items-center gap-2 mt-2">
                                        <button
                                          onClick={() => { setAdminMode('system_admin'); setAdminActiveTab(item.tab); setBellDropdownOpen(false); markRead(item.key); }}
                                          className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                                        >
                                          View <ChevronRight className="w-3 h-3" />
                                        </button>
                                        {!isRead && (
                                          <button onClick={() => markRead(item.key)} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                                            Mark read
                                          </button>
                                        )}
                                        <button onClick={() => dismissNotif(item.key, item.count)} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                                          Dismiss
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-200">
                        <button
                          onClick={() => { setAdminMode('system_admin'); setAdminActiveTab('notifications'); setBellDropdownOpen(false); }}
                          className="w-full text-center text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors py-1"
                        >
                          View All Notifications →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* simple avatar: initials */}
                  {(adminSession && effectiveAdminRole !== 'basicadmin' && adminActiveTab !== 'basic') && (
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-700">
                      {adminSession.email ? adminSession.email.split('@')[0].slice(0,2).toUpperCase() : 'AD'}
                    </div>
                  )}
                  <div className="text-sm text-gray-700 text-left">
                    <div className="font-medium">{adminSession.email}</div>
                    <div className="text-xs text-gray-500">{adminSession.role}</div>
                  </div>
                </div>
                {adminMode === 'system_admin' && <LanguageSwitcher compact />}
                <button
                  onClick={() => {
                    // reuse already-imported toast; avoid dynamic import churn
                    safeToast('success', 'Logged out successfully');
                    if (onLogout) {
                      localStorage.removeItem('dev_admin_token');
                      localStorage.removeItem('dev_admin_role');
                      dispatchOwnerTokenEvent();
                      onLogout();
                    } else {
                      handleAdminLogout();
                    }
                  }}
                  className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-xs sm:text-sm"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 min-w-0">
          {/* E-Card Display */}
          <div className="lg:col-span-1 flex justify-center">
            <div className="sticky top-4 sm:top-8">
              {adminActiveTab === 'home-customization' || adminActiveTab === 'homepage' || adminActiveTab === 'membership-plans' || adminActiveTab === 'subscriptions' ? (
                <div className="w-full max-w-md mx-auto">
                  <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <Eye className="w-5 h-5 text-green-600" />
                        <h3 className="text-lg font-bold text-gray-900">
                          {adminActiveTab === 'membership-plans' || adminActiveTab === 'subscriptions' ? 'Pricing Preview' : 'Homepage Live Preview'}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {adminActiveTab === 'membership-plans' || adminActiveTab === 'subscriptions' ? 'Subscription plan changes update in real-time' : 'Changes update in real-time'}
                      </p>
                    </div>
                    <div className="h-[600px] overflow-y-auto bg-gray-50">
                      <div className="transform scale-[0.3] origin-top-left w-[333%] h-[333%]">
                        {homepageSettings?.maintenance?.enabled ? (
                          <MaintenancePage
                            settings={homepageSettings.maintenance}
                            branding={homepageSettings.branding}
                            onNavigateToAuth={() => {}}
                          />
                        ) : (
                          <HomePage onNavigateToAuth={() => { }} customSettings={homepageSettings} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {displayUser && (displayUser.isActive || adminMode !== 'none') ? (
                    renderTemplate()
                  ) : adminMode === 'system_admin' ? (
                    <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden p-6 sm:p-8 text-center">
                      <div className="h-8 w-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-3" />
                      <p className="text-sm text-gray-600">Loading your card preview…</p>
                    </div>
                  ) : (
                    <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden p-6 sm:p-8 text-center">
                      <div className="text-red-500 mb-4">
                        <Shield className="w-12 h-12 sm:w-16 sm:h-16 mx-auto" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900 mb-2">Card Deactivated</h2>
                      <p className="text-sm sm:text-base text-gray-600 mb-4">
                        This card has been deactivated. Please upgrade your subscription to reactivate.
                      </p>
                      <button className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base">
                        Upgrade Now
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Template Info */}
        {adminActiveTab !== 'home-customization' && adminActiveTab !== 'homepage' && adminActiveTab !== 'membership-plans' && adminActiveTab !== 'subscriptions' && safeDisplayUser && (
                <div className="mt-4 sm:mt-6 text-center">
                  <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-white rounded-lg shadow-sm">
                    <span className="text-sm text-gray-600 mr-2">Template:</span>
                    <span className="font-semibold text-gray-900 text-xs sm:text-sm">
                      {(() => {
                        try {
                          const metaId = (safeDisplayUser as any)?.templateMetaId as string | undefined;
                          if (metaId) {
                            const fromState = (templatesMetaById as any)?.[metaId];
                            if (fromState && fromState.name) return fromState.name as string;
                            try {
                              const raw = localStorage.getItem('templates_cache_meta');
                              if (raw) {
                                const parsed = JSON.parse(raw) || {};
                                const m = (parsed as any)[metaId];
                                if (m && m.name) return m.name as string;
                              }
                            } catch {}
                            const fromWin = (window as any).__TEMPLATES_META_BY_ID__;
                            if (fromWin && fromWin[metaId] && fromWin[metaId].name) return fromWin[metaId].name as string;
                          }
                        } catch {}
                        // Fallback to built-in mapping by templateId
                        switch (safeDisplayUser?.templateId) {
                          case '2': return 'Elegant Looking';
                          case '3': return 'Modern and Clean';
                          case '4': return 'Golden View';
                          case '5': return 'Classic Modular';
                          default: return 'Professional Classic';
                        }
                      })()}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">
          ({(safeDisplayUser as any)?.analytics?.views || 0} views)
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Admin Panels */}
          <div className="lg:col-span-2 min-w-0 w-full max-w-full">
            

            {adminMode === 'system_admin' && (
              (() => {
                const externalHomepagePreview =
                  adminActiveTab === 'home-customization' ||
                  adminActiveTab === 'homepage' ||
                  adminActiveTab === 'membership-plans' ||
                  adminActiveTab === 'subscriptions';
                // Prefer the adminActiveTab state first, fall back to pathname-based routing
                if (adminActiveTab === 'managers' || path === '/admin/managers') {
                  const token = localStorage.getItem('dev_admin_token');
                  const role = localStorage.getItem('dev_admin_role');
                  if (!token || (role !== 'superadmin' && role !== 'basicadmin')) return <AdminLogin onLogin={(t: string, r: string | null) => { localStorage.setItem('dev_admin_token', t); localStorage.setItem('dev_admin_role', r || ''); dispatchOwnerTokenEvent(); setAdminActiveTab(r === 'basicadmin' ? 'basic' : 'overview'); }} />;
                  return <SystemAdminManagersPanel onBackToAdmin={() => setAdminActiveTab('overview')} />;
                }
                  if (adminActiveTab === 'basic' || path === '/admin/basic') {
                  const token = localStorage.getItem('dev_admin_token');
                  const role = localStorage.getItem('dev_admin_role');
                  if (!token || (role !== 'superadmin' && role !== 'basicadmin')) return <AdminLogin onLogin={(t: string, r: string | null) => { localStorage.setItem('dev_admin_token', t); localStorage.setItem('dev_admin_role', r || ''); dispatchOwnerTokenEvent(); setAdminActiveTab(r === 'basicadmin' ? 'basic' : 'overview'); }} />;
                  return (
                    <BasicAdminPanelV2
                      systemStats={systemStats}
                      liveMode={adminLiveMode}
                      selectedUsers={selectedUsers}
                      onUserSelect={handleUserSelect}
                      onSelectedUserChange={handleAdminPreviewUserChange}
                    />
                  );
                }
                if (adminActiveTab === 'requests' || path === '/admin/requests') {
                  const token = localStorage.getItem('dev_admin_token');
                  const role = localStorage.getItem('dev_admin_role');
                  if (!token || (role !== 'superadmin' && role !== 'basicadmin')) return <AdminLogin onLogin={(t: string, r: string | null) => { localStorage.setItem('dev_admin_token', t); localStorage.setItem('dev_admin_role', r || ''); dispatchOwnerTokenEvent(); setAdminActiveTab(r === 'basicadmin' ? 'basic' : 'overview'); }} />;
                  return <SystemAdminRequestsPanel onBackToAdmin={() => setAdminActiveTab('overview')} />;
                }
                // For overview and other system admin tabs, require admin token
                {
                  const token = localStorage.getItem('dev_admin_token');
                  const role = localStorage.getItem('dev_admin_role');
                  if (!token || (role !== 'superadmin' && role !== 'basicadmin')) return <AdminLogin onLogin={(t: string, r: string | null) => { localStorage.setItem('dev_admin_token', t); localStorage.setItem('dev_admin_role', r || ''); dispatchOwnerTokenEvent(); setAdminActiveTab(r === 'basicadmin' ? 'basic' : 'overview'); }} />;
                }
                return (
                  <Suspense fallback={<div className="p-6 text-sm text-gray-600">Loading admin panel…</div>}>
                  <SystemAdminPanel
                users={users}
                ads={ads}
                leads={leads}
                paymentGateways={paymentGateways}
                emailGateways={emailGateways}
                smsGateways={smsGateways}
                subscriptionPlans={subscriptionPlans}
                systemStats={systemStats}
                homepageSettings={homepageSettings}
                liveMode={adminLiveMode}
                externalHomepagePreview={externalHomepagePreview}
                showingHomepageCustomization={false}
                onSetShowingHomepageCustomization={() => {}}
                selectedUsers={selectedUsers}
                onUserSelect={handleUserSelect}
                onUpdateUser={handleUpdateUser}
                onUpdateUsers={handleUpdateUsers}
                onDeleteUsers={handleDeleteUsers}
                onUpdateAd={handleUpdateAd}
                onAddAd={handleAddAd}
                onDeleteAd={handleDeleteAd}
                onUpdatePaymentGateway={handleUpdatePaymentGateway}
                onAddPaymentGateway={handleAddPaymentGateway}
                onDeletePaymentGateway={handleDeletePaymentGateway}
                onRefreshPaymentGateways={async () => {
                  try {
                    const { paymentSettings } = await getPaymentSettings();
                    const gw = paymentSettingsToGateways(paymentSettings);
                    if (gw && gw.length) setPaymentGateways(gw);
                  } catch {}
                }}
                onUpdateSubscriptionPlan={handleUpdateSubscriptionPlan}
                onAddSubscriptionPlan={handleAddSubscriptionPlan}
                onDeleteSubscriptionPlan={handleDeleteSubscriptionPlan}
                onUpdateEmailGateway={handleUpdateEmailGateway}
                onAddEmailGateway={handleAddEmailGateway}
                onDeleteEmailGateway={handleDeleteEmailGateway}
                onUpdateSMSGateway={handleUpdateSMSGateway}
                onAddSMSGateway={handleAddSMSGateway}
                onDeleteSMSGateway={handleDeleteSMSGateway}
                onUpdateHomepageSettings={handleUpdateHomepageSettings}
                onSaveHomepageSettings={() => { handleSaveHomepageSettings(); }}
                showingHomepagePreview={showingHomepagePreview}
                onSetShowingHomepagePreview={setShowingHomepagePreview}
                editingSubscriptionPlans={editingSubscriptionPlans}
                onSetEditingSubscriptionPlans={setEditingSubscriptionPlans}
                onStartEditingSubscriptionPlans={() => setEditingSubscriptionPlans(true)}
                onStopEditingSubscriptionPlans={() => setEditingSubscriptionPlans(false)}
                onEditingSubscriptionPlansChange={setEditingSubscriptionPlans}
                onMarkLeadAsRead={handleMarkLeadAsRead}
                onDeleteLead={handleDeleteLead}
                onAddLeadTag={handleAddLeadTag}
                onViewAsCardOwner={(userId: string) => {
                  const idx = users.findIndex(u => u.id === userId);
                  if (idx !== -1) setCurrentUserIndex(idx);
                  // Track the impersonated owner and switch to card owner mode
                  setImpersonatedUserId(userId);
                  setAdminMode('card_owner');
                }}
                onActiveTabChange={setAdminActiveTab}
                activeTab={adminActiveTab}
                onSelectedUserChange={handleAdminPreviewUserChange}
                  />
                  </Suspense>
                );
              })()
            )}

            {/* Subscription Rules Modal (within App subscriptions view) */}
            {showRulesModal && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl">
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Auto-expiry Rules</h3>
                    <div className="flex items-center gap-3">
                      {(() => { try { const role = localStorage.getItem('dev_admin_role'); const email = (document.querySelector('[data-admin-email]') as HTMLElement)?.dataset?.adminEmail || ''; if (role) return <span className="text-xs text-gray-500">Signed in as {email || role} ({role})</span>; } catch {} return null; })()}
                      <button onClick={() => setShowRulesModal(false)} className="px-3 py-1 text-gray-600 hover:text-gray-900">Close</button>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-gray-600 mb-4">Set thresholds per plan. Leave a field blank to disable that limit.</p>
                    {rulesAudit && (
                      <p className="text-xs text-gray-500 mb-3">Last saved by {rulesAudit.email || rulesAudit.role || 'unknown'}{rulesAudit.at ? ` on ${new Date(rulesAudit.at).toLocaleString()}` : ''}</p>
                    )}
                    <div className="space-y-4">
                      {(subscriptionPlans && subscriptionPlans.length ? subscriptionPlans : [
                        { id: 'free', name: 'Free' },
                        { id: 'silver', name: 'Silver' },
                        { id: 'gold', name: 'Gold' },
                        { id: 'platinum', name: 'Platinum' },
                      ] as any[]).map((plan: any) => {
                        const rawKey = String(plan.name || plan.id || '').toLowerCase();
                        const key = rawKey.includes('trial') || rawKey.includes('free') ? 'free' :
                          rawKey.includes('silver') ? 'silver' :
                          rawKey.includes('gold') ? 'gold' :
                          rawKey.includes('platinum') ? 'platinum' : rawKey;
                        const DEFAULTS: AdminSubscriptionRules = {
                          free: { shares: 50, views: 100, days: 14 },
                          silver: { shares: 100, views: null, days: 28 },
                          gold: { shares: 200, views: null, days: 28 },
                          platinum: { shares: null, views: null, days: 28 },
                        } as any;
                        const base = (DEFAULTS as any)[key] || {};
                        const saved = (subRules as any)[key] || {};
                        const r = { ...base, ...saved };
                        const setField = (field: 'shares'|'views'|'days', v: string) => {
                          setSubRules(prev => ({ ...prev, [key]: { ...(prev as any)[key], [field]: v.trim() === '' ? null : Number(v) } }));
                        };
                        return (
                          <div key={key} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Plan</label>
                              <div className="font-medium text-gray-900">{plan.name || key}</div>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Shares limit</label>
                              <input type="number" min={0} value={typeof r.shares === 'number' ? r.shares : ''} onChange={(e)=> setField('shares', e.target.value)} className="w-full px-2 py-1 border rounded" placeholder="e.g., 50" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Views limit</label>
                              <input type="number" min={0} value={typeof r.views === 'number' ? r.views : ''} onChange={(e)=> setField('views', e.target.value)} className="w-full px-2 py-1 border rounded" placeholder="e.g., 100" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Days limit</label>
                              <input type="number" min={0} value={typeof r.days === 'number' ? r.days : ''} onChange={(e)=> setField('days', e.target.value)} className="w-full px-2 py-1 border rounded" placeholder="e.g., 14" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
                    <button onClick={() => setShowRulesModal(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
                    <button
                      onClick={async () => {
                        const token = localStorage.getItem('dev_admin_token');
                        const role = localStorage.getItem('dev_admin_role');
                        const email = (document.querySelector('[data-admin-email]') as HTMLElement)?.dataset?.adminEmail || '';
                        if (!token || !role) {
                          safeToast('error', 'Admin session required');
                          return;
                        }
                        if (role !== 'superadmin') {
                          safeToast('error', 'Only Super Admin can save subscription rules');
                          return;
                        }
                        setRulesSaving(true);
                        try {
                          await updateAdminSiteSettings({ subscriptionRules: subRules as any });
                          safeToast('success', `Subscription rules saved by ${email || role}`);
                          setShowRulesModal(false);
                        } catch (e) {
                          safeToast('error', 'Failed to save rules');
                        } finally {
                          setRulesSaving(false);
                        }
                      }}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                      disabled={rulesSaving || rulesLoading}
                    >{rulesSaving ? 'Saving…' : 'Save Rules'}</button>
                  </div>
                </div>
              </div>
            )}

            {adminMode === 'card_owner' && displayUser && (
              <Suspense fallback={<div className="p-4 text-sm text-gray-500">Loading owner tools…</div>}>
                <CardOwnerPanel
                  user={displayUser}
                  onUpdateUser={handleUpdateUser}
                  onPreviewChange={setPreviewUser}
                  leads={leads}
                  leadsUnauthorized={leadsUnauthorized}
                  onMarkLeadAsRead={handleMarkLeadAsRead}
                  onDeleteLead={handleDeleteLead}
                  onAddLeadTag={handleAddLeadTag}
                  requestedTab={ownerRequestedTab}
                  onRequestedTabConsumed={() => setOwnerRequestedTab(null)}
                />
              </Suspense>
            )}

            {adminMode === 'none' && displayUser && (
              <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
                <div className="text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Eye className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">View Mode</h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-6">
                    You're viewing this digital business card as a visitor would see it.
                  </p>

                  <div className="bg-blue-50 p-3 sm:p-4 rounded-lg mb-6">
                    <h3 className="text-sm sm:text-base font-semibold text-blue-900 mb-2">Available Actions:</h3>
                    <ul className="text-xs sm:text-sm text-blue-800 space-y-1">
                      <li>• Call, Email, or Visit Website</li>
                      <li>• Generate and download QR code</li>
                      <li>• Share the business card</li>
                      <li>• Save contact information</li>
                      <li>• Send messages via contact form</li>
                    </ul>
                  </div>

                  <div className="text-xs sm:text-sm text-gray-500">
                    <p className="mb-2">
                      <strong>Current User:</strong> {displayUser.name}
                    </p>
                    <p className="mb-2">
                      <strong>Template:</strong> {
                        displayUser.templateId === '1' ? 'Professional Classic' :
                          displayUser.templateId === '2' ? 'Elegant Looking' :
                            displayUser.templateId === '3' ? 'Modern and Clean' :
                              displayUser.templateId === '4' ? 'Golden View' :
                                'Classic Modular'
                      }
                    </p>
                    <p>
                      <strong>Views:</strong> {displayUser.analytics?.views || 0}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- CRM Contact Exchange public route ---------- */
function ContactExchangeRoute() {
  const { salesRepId } = useParams<{ salesRepId: string }>();
  return <ContactExchange salesRepId={salesRepId || ''} />;
}

/* ---------- Legacy /dashboard redirect ---------- */
function DashboardRedirect() {
  const loc = useLocation();
  return <Navigate to={`/website/dashboard${loc.search}`} replace />;
}

/* ---------- Payment Result Page — PesaPal TCard subscription callback ---------- */
function PaymentResultPage() {
  const [status, setStatus] = React.useState<'verifying' | 'success' | 'pending' | 'failed'>('verifying');
  const [message, setMessage] = React.useState('Verifying your payment...');

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const txId = params.get('txId') || '';
    const provider = params.get('provider') || '';

    if (!txId) {
      setStatus('failed');
      setMessage('Payment reference not found. Please check your subscription status.');
      return;
    }

    // Poll the transaction status endpoint to confirm payment before showing success.
    // The server has already verified with PesaPal in the callback handler, but we
    // re-confirm here to guard against any race condition or direct URL access.
    const maxAttempts = 12;
    let attempt = 0;

    const poll = async () => {
      attempt++;
      try {
        const token = typeof window !== 'undefined'
          ? (localStorage.getItem('owner_token') || '') : '';
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(`/api/payments/transactions/${encodeURIComponent(txId)}`, { headers });
        if (!res.ok) throw new Error('fetch_failed');
        const data = await res.json();
        const txStatus = data?.tx?.status;

        if (txStatus === 'paid') {
          setStatus('success');
          setMessage('Payment confirmed! Your subscription is now active.');
          // Redirect to the owner panel after a short delay so the user sees the success message
          setTimeout(() => { window.location.replace('/?payment_success=1&provider=' + encodeURIComponent(provider)); }, 2500);
          return;
        }
        if (txStatus === 'failed' || txStatus === 'refunded') {
          setStatus('failed');
          setMessage('Payment could not be confirmed. Please check your subscription or try again.');
          return;
        }
        // Still pending — retry
        if (attempt < maxAttempts) {
          setTimeout(poll, 2500);
        } else {
          // Timed out; the webhook/IPN may still arrive. Send user back to the panel.
          setStatus('pending');
          setMessage('Payment is still being processed. Your subscription will activate shortly. Redirecting...');
          setTimeout(() => { window.location.replace('/?payment_pending=1&txId=' + encodeURIComponent(txId)); }, 3000);
        }
      } catch {
        if (attempt < maxAttempts) {
          setTimeout(poll, 3000);
        } else {
          setStatus('pending');
          setMessage('Could not verify payment status. Your subscription will activate once the payment is confirmed. Redirecting...');
          setTimeout(() => { window.location.replace('/'); }, 3000);
        }
      }
    };

    // Small initial delay to allow the server callback handler to finish
    setTimeout(poll, 1500);
  }, []);

  const iconClass = status === 'success' ? 'text-green-500' : status === 'failed' ? 'text-red-500' : 'text-blue-500';
  const bgClass = status === 'success' ? 'bg-green-50 border-green-200' : status === 'failed' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200';
  const title = status === 'success' ? 'Payment Successful!' : status === 'failed' ? 'Payment Failed' : 'Verifying Payment...';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className={`max-w-md w-full rounded-2xl border-2 p-8 text-center shadow-lg ${bgClass}`}>
        {(status === 'verifying' || status === 'pending') && (
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        )}
        {status === 'success' && (
          <svg className={`w-16 h-16 mx-auto mb-4 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
        {status === 'failed' && (
          <svg className={`w-16 h-16 mx-auto mb-4 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
        <h2 className="text-2xl font-bold text-gray-900 mb-3">{title}</h2>
        <p className="text-gray-600">{message}</p>
        {status === 'failed' && (
          <a
            href="/"
            className="mt-6 inline-block px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold"
          >
            Back to Dashboard
          </a>
        )}
      </div>
    </div>
  );
}

/* ---------- App with routing ---------- */
function WebAppManifestSync() {
  const location = useLocation();
  useEffect(() => {
    syncWebAppManifest({ pathname: location.pathname });
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <SiteBrandingProvider>
    <WebAppManifestSync />
    <Routes>
  {/* Home Page at root; Auth flow under /auth — Suspense so lazy route chunks can load */}
  <Route path="/" element={<Suspense fallback={<RouteChunkFallback />}><HomeRoute /></Suspense>} />
  <Route path="/auth" element={<Suspense fallback={<RouteChunkFallback />}><AuthFlow /></Suspense>} />
  {/* Mobile Sales App (offline-capable PWA for field sales teams) */}
  <Route path="/mobile/*" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" /></div>}><MobileApp /></Suspense>} />
  {/* Direct login deep-links */}
  <Route path="/owner-login" element={<Navigate to="/auth?panel=owner&login=1" replace />} />
  <Route path="/card-owner-login" element={<Navigate to="/auth?panel=owner&login=1" replace />} />
  <Route path="/admin-login" element={<Navigate to="/auth?panel=admin&login=1" replace />} />
  <Route path="/basic-admin-login" element={<Navigate to="/auth?panel=basic-admin&login=1" replace />} />
  <Route path="/system-admin-login" element={<Navigate to="/auth?panel=system-admin&login=1" replace />} />
  {/* CRM standalone login & panel */}
  <Route path="/crm-login" element={<CrmLoginPage />} />
  <Route path="/crm-login/:solution" element={<CrmLoginPage />} />
  <Route path="/field-team-management" element={<CrmLoginPage />} />
  <Route path="/field-operations" element={<Navigate to="/field-team-management" replace />} />
  <Route path="/sales-team-tracking" element={<CrmLoginPage />} />
  <Route path="/field-service-management" element={<CrmLoginPage />} />
  <Route path="/technician-tracking" element={<Navigate to="/field-service-management" replace />} />
  <Route path="/security-workforce-management" element={<CrmLoginPage />} />
  <Route path="/supervisor-management" element={<CrmLoginPage />} />
  <Route path="/cleaning-workforce-management" element={<CrmLoginPage />} />
  <Route path="/field-workforce-management" element={<CrmLoginPage />} />
  <Route path="/crm-lead-management" element={<CrmLoginPage />} />
  <Route path="/sales-crm" element={<Navigate to="/crm" replace />} />
  <Route path="/crm" element={<Suspense fallback={<RouteChunkFallback />}><CrmPanelPage /></Suspense>} />
  {/* Support */}
  <Route path="/faq" element={<Suspense fallback={<RouteChunkFallback />}><FAQPage /></Suspense>} />
  <Route path="/terms" element={<Suspense fallback={<RouteChunkFallback />}><TermsOfServicePage /></Suspense>} />
  <Route path="/privacy" element={<Suspense fallback={<RouteChunkFallback />}><PrivacyPolicyPage /></Suspense>} />
  <Route path="/delete-account" element={<Suspense fallback={<RouteChunkFallback />}><DeleteAccountPage /></Suspense>} />
  <Route path="/data-deletion" element={<Suspense fallback={<RouteChunkFallback />}><DeleteAccountPage /></Suspense>} />
  <Route path="/refund-policy" element={<Suspense fallback={<RouteChunkFallback />}><RefundPolicyPage /></Suspense>} />
  {/* Payment result landing page — used by PesaPal TCard subscription callback */}
  <Route path="/payment-result" element={<PaymentResultPage />} />
  {/* APK Download */}
  <Route path="/download" element={<Suspense fallback={<RouteChunkFallback />}><DownloadPage /></Suspense>} />
  {/* Blog */}
  <Route path="/blog" element={<Suspense fallback={<RouteChunkFallback />}><BlogPostPage /></Suspense>} />
  <Route path="/blog/:slug" element={<Suspense fallback={<RouteChunkFallback />}><BlogPostPage /></Suspense>} />

  {/* Mini Website - public view (subdomain: {slug}.tcards.org/web) */}
  <Route path="/web/product/:itemSlug" element={<React.Suspense fallback={<RouteChunkFallback />}><PublicMiniWebsite itemType="product" /></React.Suspense>} />
  <Route path="/web/service/:itemSlug" element={<React.Suspense fallback={<RouteChunkFallback />}><PublicMiniWebsite itemType="service" /></React.Suspense>} />
  <Route path="/web" element={<React.Suspense fallback={<RouteChunkFallback />}><PublicMiniWebsite /></React.Suspense>} />
  {/* Mini Website - fallback path-based view */}
  <Route path="/site/:slug/product/:itemSlug" element={<React.Suspense fallback={<RouteChunkFallback />}><PublicMiniWebsite itemType="product" /></React.Suspense>} />
  <Route path="/site/:slug/service/:itemSlug" element={<React.Suspense fallback={<RouteChunkFallback />}><PublicMiniWebsite itemType="service" /></React.Suspense>} />
  <Route path="/site/:slug" element={<React.Suspense fallback={<RouteChunkFallback />}><PublicMiniWebsite /></React.Suspense>} />
  {/* Website Owner Dashboard */}
  <Route path="/website/dashboard" element={<React.Suspense fallback={<RouteChunkFallback />}><MiniWebsiteDashboard /></React.Suspense>} />
  {/* Mini Website Landing Page */}
  <Route path="/miniwebsite" element={<MiniWebsiteLanding />} />
  {/* Digital Business Card product landing */}
  <Route path="/digital-card" element={<DigitalCardLanding />} />
  <Route path="/templates" element={<TemplatesGalleryPage />} />
  <Route path="/templates/:category" element={<TemplatesGalleryPage />} />
  <Route path="/contact" element={<DigitalCardContactPage />} />
  {/* /website/ redirects to landing */}
  <Route path="/website" element={<Navigate to="/miniwebsite" replace />} />
  {/* Legacy /dashboard redirect — old Stripe checkout URLs used this path */}
  <Route path="/dashboard" element={<DashboardRedirect />} />
  {/* Website Owner Login */}
  <Route path="/website/login" element={<React.Suspense fallback={<RouteChunkFallback />}><WebsiteOwnerLogin /></React.Suspense>} />

      {/* Card preview route (standalone, must implement CardPreview to read params) */}
      <Route path="/card/:userId" element={<Suspense fallback={<RouteChunkFallback />}><CardPreview /></Suspense>} />

  {/* Public card by slug */}
  {/* Dev-only preview route: view a card by explicit handle + username */}
  <Route path="/dev/preview/:handle/:username" element={<Suspense fallback={<RouteChunkFallback />}><PublicCardViewer /></Suspense>} />
  {/* Public viewer by username under current host's subdomain */}
  <Route path="/:cardSlug" element={<Suspense fallback={<RouteChunkFallback />}><PublicCardViewer /></Suspense>} />

      <Route path="/set-password" element={<Suspense fallback={<RouteChunkFallback />}><SetPassword /></Suspense>} />
  {/* CRM: Public contact exchange (prospect scans QR) */}
  <Route path="/exchange/:salesRepId" element={<Suspense fallback={<RouteChunkFallback />}><ContactExchangeRoute /></Suspense>} />
  {/* Unknown routes -> home */}
  <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </SiteBrandingProvider>
  );
}

// Lightweight wrapper to host HomePage outside AuthFlow
const HomeRoute: React.FC = () => {
  const navigate = useNavigate();
  const { homepageSettings: brandingHp } = useSiteBranding();
  const [homepageSettings, setHomepageSettings] = useState<any>(() => brandingHp);

  useEffect(() => {
    setHomepageSettings((prev: any) => ({ ...brandingHp, ...prev, branding: brandingHp.branding }));
  }, [brandingHp]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('/api/public/subscription-plans').catch(() => null as Response | null);
        let hp = mergeHomepageSettings(homepageSettings);

        const mergeCachedPlansIfNeeded = () => {
          const existing = Array.isArray(hp?.pricing?.plans) ? hp.pricing.plans : [];
          if (!existing.length) {
            try {
              const cachedRaw = localStorage.getItem('server_plans_cache');
              if (cachedRaw) {
                const cachedArr = JSON.parse(cachedRaw);
                if (Array.isArray(cachedArr) && cachedArr.length > 0) {
                  hp = { ...hp, pricing: { ...(hp.pricing || {}), plans: cachedArr } };
                }
              }
            } catch {}
          }
        };

        try {
          if (resp && resp.ok) {
            const plans = await resp.json();
            if (Array.isArray(plans) && plans.length > 0) {
              hp = { ...hp, pricing: { ...(hp.pricing || {}), plans } };
              try { localStorage.setItem('server_plans_cache', JSON.stringify(plans)); } catch {}
            } else {
              mergeCachedPlansIfNeeded();
            }
          } else {
            mergeCachedPlansIfNeeded();
          }
        } catch {
          mergeCachedPlansIfNeeded();
        }
        try {
          const raw = localStorage.getItem('homepageSettings');
          if (raw) {
            const localHp = JSON.parse(raw);
            if (localHp?.maintenance) {
              const { enabled: _ignoreEnabled, ...restLocalMaint } = localHp.maintenance || {};
              hp = { ...hp, maintenance: { ...(hp.maintenance || {}), ...restLocalMaint } };
            }
          }
        } catch {}
        setHomepageSettings(hp);
        if (hp?.pricing?.plans) {
          try { localStorage.setItem('server_plans_cache', JSON.stringify(hp.pricing.plans)); } catch {}
        }
      } catch {}
    })();
  }, [brandingHp]);
  const onNavigateToAuth = (type: 'admin' | 'user', opts?: { selectedPlan?: PaymentPlan }) => {
    if (opts?.selectedPlan) {
      try { localStorage.setItem('selectedPlan', JSON.stringify(opts.selectedPlan)); } catch {}
    }
    if (type === 'admin') {
      navigate('/auth?admin=1');
    } else {
      navigate('/auth?login=1');
    }
  };
  // While loading, prefer last known server plans from cache to avoid mock or empty display
  const initialFromCache = (() => {
    try {
      const def: any = { ...(defaultHomepageSettings as any) };
      const raw = localStorage.getItem('homepage_server_cache');
      if (raw) {
        const parsed = JSON.parse(raw);
        const hp = (parsed && parsed.homepageSettings) || {};
        let merged = { ...def, ...hp, pricing: { ...def.pricing, ...(hp.pricing || {}) } };
        // Overlay local maintenance settings if saved
        try {
          const localRaw = localStorage.getItem('homepageSettings');
          if (localRaw) {
            const localHp = JSON.parse(localRaw);
            if (localHp?.maintenance) {
              // Keep server authoritative for maintenance.enabled; merge other local fields only
              const { enabled: _ignoreEnabled, ...restLocalMaint } = (localHp.maintenance || {});
              merged = { ...merged, maintenance: { ...(merged.maintenance || {}), ...restLocalMaint } };
            }
          }
        } catch {}
        if (Array.isArray(hp?.pricing?.plans)) return merged;
        // fallback: if no plans in hp, try a direct plans cache
        const rawPlans = localStorage.getItem('server_plans_cache');
        if (rawPlans) {
          const plans = JSON.parse(rawPlans);
          return { ...merged, pricing: { ...merged.pricing, plans: Array.isArray(plans) ? plans : [] } };
        }
        return merged;
      }
    } catch {}
    return defaultHomepageSettings as any;
  })();
  const effective = homepageSettings || brandingHp || initialFromCache;

  // Subdomain: miniwebsite.tcards.org → show MiniWebsite landing page
  const host = window.location.hostname.toLowerCase();
  if (/^(?:www\.)?miniwebsite\.tcards\.org$/.test(host)) {
    return <MiniWebsiteLanding />;
  }

  // If maintenance is enabled, show the Coming Soon page instead of Home
  if (effective?.maintenance?.enabled) {
    return (
      <MaintenancePage
        settings={effective.maintenance}
        branding={effective.branding}
        onNavigateToAuth={onNavigateToAuth}
      />
    );
  }
  return <HomePage onNavigateToAuth={onNavigateToAuth} customSettings={effective} />;
};

export default App;